export interface Pillar {
  id: string;
  number: string;
  title: string;
  subtitle: string;
  tagline: string;
  items: {
    name: string;
    description: string;
    features: string[];
    link: string;
  }[];
}

export interface DomainPricing {
  extension: string;
  register: number;
  transfer: number;
  renew: number;
  popular?: boolean;
  bestFor: string;
}

export interface HostingPlan {
  id: string;
  name: string;
  priceYear: number;
  badge?: string;
  targetAudience: string;
  specs: {
    disk: string;
    ram: string;
    domain: string;
    ssl: string;
    email: string;
    bandwidth: string;
    websites: string;
    cpanel: boolean;
    mpesa: boolean;
    backups: string;
  };
  features: string[];
}

export interface CaseStudy {
  id: string;
  category: string;
  client: string;
  title: string;
  summary: string;
  description: string;
  industry: string;
  domain?: string;
  domainSnippet?: {
    tagline: string;
    keyFeatures: string[];
    liveUrl: string;
  };
  metrics: { label: string; value: string }[];
  deliverables: string[];
  techStack: string[];
  quote?: string;
  accentColor: string;
  imageUrl?: string;
}

export interface Review {
  id: string;
  author: string;
  date: string;
  rating: number;
  content: string;
  ownerReply?: string;
  source: string;
}

export interface IndustryItem {
  id: string;
  name: string;
  iconName: string;
  subtitle: string;
  description: string;
  solutionsProvided: string[];
  sampleMetric: string;
}

export interface ErpSolution {
  id: string;
  title: string;
  excelSheetName: string;
  excelPainPoints: string[];
  appFeatures: string[];
  sampleData: { [key: string]: string | number }[];
  metrics: { label: string; value: string };
}

export interface Article {
  id: string;
  slug: string;
  title: string;
  kicker: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  author: string;
  content: string[];
  wordCount: number;
  seoKeywords: string[];
  featuredImage?: string;
}

export interface StoreProduct {
  id: string;
  name: string;
  category: string;
  priceKes: number;
  badge?: string;
  description: string;
  specifications: string[];
  inStock: boolean;
  warranty: string;
  leadTime: string;
  imageUrl: string;
}

export interface PricePackage {
  id: string;
  name: string;
  category: 'websites' | 'systems' | 'design' | 'hosting';
  categoryLabel: string;
  priceKes: number;
  priceDisplay: string;
  pricePeriod?: string;
  isStartingAt?: boolean;
  description: string;
  features: string[];
  popular?: boolean;
  lipaPolePole?: boolean;
  turnaround: string;
}

export interface PartnerItem {
  id: string;
  name: string;
  category: string;
  domain?: string;
  logoText: string;
  description: string;
  badge?: string;
}

export const PARTNERS_LIST: PartnerItem[] = [
  {
    id: "shantis-designs",
    name: "Shantis Designs Solar Solutions",
    category: "Renewable Energy & Solar E-Commerce",
    domain: "shantisdesigns.co.ke",
    logoText: "SHANTIS SOLAR",
    description: "Leading Kenyan commercial and residential solar installation, hybrid inverters & storage provider.",
    badge: "Solar Tech"
  },
  {
    id: "concept-business-central",
    name: "Concept Business Central",
    category: "Corporate Enterprise & Commercial Advisory",
    logoText: "CONCEPT BUSINESS CENTRAL",
    description: "Executive commercial consulting, corporate enterprise advisory, and business acceleration.",
    badge: "Enterprise"
  },
  {
    id: "topsec-technologies",
    name: "Topsec Technologies",
    category: "Cybersecurity & Physical Infrastructure",
    domain: "topsectechnologies.co.ke",
    logoText: "TOPSEC TECHNOLOGIES",
    description: "Advanced enterprise cybersecurity, CCTV telemetry, biometric systems, and network integrity.",
    badge: "Cybersecurity"
  },
  {
    id: "shamba-flow",
    name: "Shamba Flow",
    category: "AgriTech & Cold Chain Supply",
    domain: "shambaflow.co.ke",
    logoText: "SHAMBA FLOW",
    description: "Smart agricultural supply chain, produce traceability, and farm-to-market logistics platform.",
    badge: "AgriTech"
  },
  {
    id: "fancy-touch-beauty",
    name: "Fancy Touch Beauty",
    category: "Luxury Beauty & Retail Commerce",
    domain: "fancytouchbeauty.co.ke",
    logoText: "FANCY TOUCH",
    description: "Premium beauty brand, salon e-commerce catalog, and omnichannel booking system.",
    badge: "E-Commerce"
  },
  {
    id: "kenya-professional-trainers",
    name: "Kenya Professional Trainers",
    category: "Executive Training & Certification",
    logoText: "KENYA PROFESSIONAL TRAINERS",
    description: "Accredited corporate training, leadership workshops, and institutional development.",
    badge: "Education"
  },
  {
    id: "sirman-foundation",
    name: "Sirman Foundation",
    category: "Non-Profit & Community Impact",
    logoText: "SIRMAN FOUNDATION",
    description: "Youth empowerment, educational access scholarships, and sustainable development initiatives.",
    badge: "Foundation"
  },
  {
    id: "teen-network-magazine",
    name: "Teen Network Magazine",
    category: "Youth Media & Digital Publishing",
    logoText: "TEEN NETWORK MAGAZINE",
    description: "Continental digital publication, creative media production, and youth culture journalism.",
    badge: "Publishing"
  }
];

export const SITE_CONTACT = {
  phone: "+254 114646341",
  phoneFormatted: "+254 114 646 341",
  email: "deals@thinkersarcade.co.ke",
  adminNotificationEmail: "Brayo806@gmail.com",
  hours: "Mon–Fri 9:00 AM – 10:00 PM",
  location: "Nairobi, Kenya",
  whatsappUrl: "https://wa.me/254114646341?text=Hello%20Thinkers%20Arcade%20Team%2C%20I%20would%20like%20to%20discuss%20a%20project.",
  // Primary billing & client portal domain:
  portalUrl: "https://hosting.thinkersarcade.co.ke/",
  orderUrl: "https://hosting.thinkersarcade.co.ke/order",
  cartUrl: "https://hosting.thinkersarcade.co.ke/cart.php",
  supportUrl: "https://hosting.thinkersarcade.co.ke/support/kb",
  clientAreaUrl: "https://hosting.thinkersarcade.co.ke/clientarea.php",
  // Direct Paybill Payment Details:
  paybillNumber: "542542",
  accountNumber: "01550",
  bankName: "I&M (Hosting account)",
  paybillFormattedInstructions: "Pay via M-Pesa Paybill 542542, Account 01550 (I&M Hosting Account)",
  googlePlaceId: "ChIJr9Av3PYrBmIR4Tfmygy5Hfw",
  googleReviewUrl: "https://admin.trustindex.io/api/googleWriteReview?place-id=ChIJr9Av3PYrBmIR4Tfmygy5Hfw",
  googleMapsUrl: "https://www.google.com/maps/search/?api=1&query=Thinkers+Arcade+Nairobi&query_place_id=ChIJr9Av3PYrBmIR4Tfmygy5Hfw"
};

export const ERP_WORKFLOWS: ErpSolution[] = [
  {
    id: "inventory",
    title: "Multi-Location Warehouse & Inventory App",
    excelSheetName: "Master_Inventory_2025_Final_v3_BROKEN.xlsx",
    excelPainPoints: [
      "No barcode scanning; manual typing causes SKU mismatches",
      "Stock levels out of sync between Nairobi warehouse and Mombasa branch",
      "Spreadsheet crashes when 4 team members open it on Google Drive",
      "Zero audit trail when items go missing or formulas are deleted"
    ],
    appFeatures: [
      "Instant mobile camera barcode & QR scanner",
      "Multi-branch live stock sync with automatic low-stock WhatsApp alert",
      "Role-based access: warehouse staff only see physical counts, managers see cost prices",
      "Direct integration with suppliers for 1-click purchase orders"
    ],
    sampleData: [
      { sku: "CAT6-500M", item: "Outdoor STP Cable 500m", nairobiStock: 142, mombasaStock: 38, status: "In Stock" },
      { sku: "FIB-SFP-10G", item: "10G BiDi SFP+ Transceiver", nairobiStock: 18, mombasaStock: 4, status: "Reorder Required" },
      { sku: "UPS-3KVA-ON", item: "Online Rackmount 3kVA UPS", nairobiStock: 12, mombasaStock: 9, status: "In Stock" }
    ],
    metrics: { label: "Discrepancy Reduction", value: "99.2%" }
  },
  {
    id: "reconciliation",
    title: "Daily Sales & Daraja M-Pesa Automated Cashier",
    excelSheetName: "Daily_Cashier_Till_Reconciliation_July.xlsx",
    excelPainPoints: [
      "Staff manually copy 10-character M-Pesa codes from SMS into Excel",
      "Fake SMS payment fraud goes unnoticed until end of month",
      "End-of-day reconciliation takes cashier 2.5 hours every evening",
      "No instant receipt sent to customer's WhatsApp"
    ],
    appFeatures: [
      "Direct Daraja C2B webhook auto-matches transaction code to invoices",
      "Instant push notification and WhatsApp e-receipt to customer in 3 seconds",
      "End-of-day reconciliation completed in 1 click (100% mathematical accuracy)",
      "Automated KRA eTIMS invoice generation and tax compliance"
    ],
    sampleData: [
      { time: "14:22", ref: "SJH82K19", customer: "Kariuki Enterprises", amount: "KSh 45,000", match: "Auto-Verified" },
      { time: "14:48", ref: "SJH90Q41", customer: "Afritech Solutions", amount: "KSh 128,500", match: "Auto-Verified" },
      { time: "15:05", ref: "SJI11B02", customer: "Coastal Logistics", amount: "KSh 32,000", match: "Auto-Verified" }
    ],
    metrics: { label: "Hours Saved per Month", value: "65 hrs" }
  },
  {
    id: "procurement",
    title: "Construction & Hardware RFQ Procurement System",
    excelSheetName: "Contractor_RFQ_Quotes_Master.xlsx",
    excelPainPoints: [
      "Suppliers send quotes in 15 different PDF/Excel formats",
      "Comparing prices requires 6 browser tabs and manual copy-pasting",
      "Overpayments due to forgotten price agreements with preferred vendors",
      "Project managers approve orders on phone with no written audit log"
    ],
    appFeatures: [
      "Vendor bidding portal where suppliers submit rates directly into your database",
      "Side-by-side unit price matrix with automatic lowest-cost recommendation",
      "2-level managerial approval workflow before LPO generation",
      "Delivery tracking with photo proof of dispatch on site"
    ],
    sampleData: [
      { material: "Steel Y12 Bars", qty: "400 pcs", lowestBidder: "Apex Steels", rate: "KSh 1,420", saving: "11.4%" },
      { material: "Portland Cement 50kg", qty: "850 bags", lowestBidder: "Bamburi Direct", rate: "KSh 720", saving: "7.8%" },
      { material: "Ballast 20mm (Ton)", qty: "40 Tons", lowestBidder: "Meru Quarries", rate: "KSh 2,100", saving: "15.0%" }
    ],
    metrics: { label: "Procurement Cost Reduction", value: "8.5%" }
  },
  {
    id: "fleet",
    title: "Fleet Operations & Trip Dispatch Manager",
    excelSheetName: "Fleet_Mileage_Fuel_Logbook.xlsx",
    excelPainPoints: [
      "Drivers record odometer readings and fuel cash receipts on crumpled paper",
      "Vehicle service intervals and insurance renewals are missed regularly",
      "Fuel theft goes undetected due to lack of km/liter cross-check",
      "No real-time tracking of which driver has which truck"
    ],
    appFeatures: [
      "Mobile driver check-in with odometer photo verification",
      "Automated fuel consumption anomalies detector with GPS distance cross-match",
      "Automated WhatsApp notification 14 days before NTSA inspection & insurance expire",
      "Instant digital trip manifests and delivery confirmation"
    ],
    sampleData: [
      { vehicle: "KCY 412B (Isuzu FRR)", driver: "John Kamau", fuelEfficiency: "4.8 km/L", status: "Active Route (Kisumu)" },
      { vehicle: "KDE 801T (Canter 3T)", driver: "Moses Omondi", fuelEfficiency: "7.1 km/L", status: "Loading (Embakasi)" },
      { vehicle: "KDF 119P (Hilux 4WD)", driver: "Peter Mutua", fuelEfficiency: "9.2 km/L", status: "Service Scheduled" }
    ],
    metrics: { label: "Fuel Theft Prevention", value: "-22%" }
  }
];

export const INSIGHTS_ARTICLES: Article[] = [
  {
    id: "local-seo-nairobi-guide",
    slug: "complete-guide-to-local-seo-in-nairobi-kenya",
    kicker: "Search Infrastructure",
    title: "The 2026 Guide to Dominating Local SEO & Google Maps in Kenya",
    excerpt: "An actionable, technical playbook to capture high-intent commercial searches across Nairobi, Mombasa, Kisumu, Nakuru, and Eldoret.",
    category: "SEO",
    readTime: "8 min read",
    wordCount: 880,
    date: "September 2026",
    author: "Thinkers Arcade Growth Team",
    featuredImage: "/images/hero_digital_infrastructure_1790951298995.webp",
    seoKeywords: ["SEO services Nairobi Kenya", "SEO company Kenya", "Google Business Profile optimization Nairobi", "rank on Google Kenya", "Local SEO Kenya"],
    content: [
      "In the competitive Kenyan digital marketplace, ranking on the first page of Google is no longer a vanity metric — it is the single most predictable driver of qualified commercial inbound inquiries. Whether you operate an ISP in Westlands, an enterprise law firm in Upper Hill, or a hardware distributor along Commercial Street in Industrial Area, high-intent buyers turn to Google with location-specific queries such as 'best corporate law firm Nairobi' or 'fast fiber internet installation near me'.",
      "Traditional SEO advice from Western blogs frequently fails in East Africa because it assumes high desktop penetration and static local data. In Kenya, over 82% of all commercial searches originate from mobile devices connected to Safaricom 4G/5G or Airtel networks. Google's mobile crawler prioritizes three foundational elements in Kenya: hyper-localized entity citations, verified Google Business Profile signals, and sub-second Core Web Vitals on Kenyan mobile IP ranges.",
      "1. Entity Optimization & Google Business Profile Architecture: Your Google Business Profile (formerly Google My Business) is your commercial storefront. Most Kenyan companies make the fatal mistake of listing an ambiguous business category or omitting their exact physical coordinates. To dominate the 3-Pack Maps carousel, your primary category must match your exact commercial intent (e.g. 'Marketing Agency', 'Software Company', or 'Solar Energy Equipment Supplier'). Your address must align perfectly with your KeNIC whois registration, KRA PIN tax location, and business permit records across government directories.",
      "2. Geo-Targeted Commercial Landing Pages: Relying exclusively on your homepage to rank for multiple cities across East Africa dilutes your topical authority. High-growth enterprises build structured programmatic hubs for each operational node — Nairobi, Mombasa, Kisumu, Nakuru, Eldoret, and Meru. Each location page must feature authentic client testimonials from that locality, specific delivery or deployment radius maps, local payment options (such as direct M-Pesa Paybill numbers), and unique Schema.org LocalBusiness JSON-LD markup containing exact geo-coordinates (latitude and longitude).",
      "3. Core Web Vitals on East African Mobile Networks: If your website takes longer than 2.2 seconds to render on an ordinary 4G connection in Nairobi, over 40% of prospective clients will bounce back to Google's search results and click on your competitor. At Thinkers Arcade, our engineering stack eliminates heavy WordPress visual builders in favor of clean TypeScript, Tailwind CSS, and edge-cached NVMe LiteSpeed servers. This guarantees a Largest Contentful Paint (LCP) under 0.8s and a Cumulative Layout Shift (CLS) near zero.",
      "4. Managing Verified Google Reviews & Trust Signals: Google's ranking algorithm strongly rewards review velocity and owner engagement. Notice how verified Google reviews with detailed text and direct owner replies boost local pack rankings. When happy clients leave reviews mentioning specific services ('website development', 'SEO optimization', 'POS setup'), Google's natural language processing associates those semantic entities directly with your brand.",
      "Conclusion & Commercial Next Steps: Businesses that invest in systematic, technical SEO build compounding digital assets that dramatically reduce their reliance on expensive paid ads. A focused 90-day search sprint with Thinkers Arcade establishes the technical baseline, schema graph, and local citations required to secure and defend top rankings."
    ]
  },
  {
    id: "litespeed-nvme-kenya",
    slug: "why-litespeed-nvme-hosting-is-faster-for-kenyan-ecommerce",
    kicker: "Cloud Infrastructure",
    title: "Why LiteSpeed NVMe Hosting Outperforms Standard Servers Across Kenya",
    excerpt: "How server-side LSCache, KIXP local peering, and HTTP/3 protocol eliminate M-Pesa checkout drop-offs across Safaricom and Airtel mobile networks.",
    category: "Cloud & Hosting",
    readTime: "7 min read",
    wordCount: 820,
    date: "September 2026",
    author: "Tonny Njeru · Lead Systems Architect",
    featuredImage: "/images/cloud_enterprise_servers_1790953244864.webp",
    seoKeywords: ["web hosting Kenya", "NVMe LiteSpeed hosting Nairobi", "fastest website hosting Kenya", "M-Pesa checkout speed", "cPanel hosting Kenya"],
    content: [
      "In East African digital commerce, speed is not merely a user convenience — it is the direct determinant of your payment gateway conversion rate. When a Kenyan shopper clicks 'Pay with M-Pesa' on your e-commerce checkout, an STK push must execute through the Safaricom Daraja API within seconds. If the hosting server lags, the API connection times out, leaving the customer confused and abandoning the transaction.",
      "The Legacy Problem: Why Traditional Kenyan Hosting Fails: For decades, web hosting providers in Nairobi resold shared Apache servers mounted on mechanical spinning hard disk drives (HDD) located in Europe or the US. When a user in Nairobi visits a website hosted on a server in Frankfurt or London, every data packet must travel across undersea fiber cables through Mombasa, Suez, and the Mediterranean. This incurs an unavoidable physical latency penalty of 160ms to 240ms per round trip. When multiplied across 80 CSS, JS, and image requests on a typical page, load times routinely balloon to 6–10 seconds.",
      "The LiteSpeed + Enterprise NVMe Advantage: LiteSpeed Web Server is a drop-in, high-performance replacement for Apache. It uses an asynchronous, event-driven architecture that serves thousands of concurrent visitors using minimal CPU and memory. When paired with PCIe Gen4 NVMe (Non-Volatile Memory Express) solid-state storage, read/write speeds exceed 7,000 MB/s — over 14 times faster than standard SATA SSDs.",
      "Local Peering at the Kenya Internet Exchange Point (KIXP): Thinkers Arcade hosting infrastructure utilizes direct peering at KIXP in Nairobi. This means traffic between Safaricom, Airtel, Telkom Kenya, and Jamii Telecommunications stays inside Kenya. Local ping latency plummets from 180ms down to 8ms. When a customer in Westlands browses your product catalog, pages render instantaneously.",
      "Native HTTP/3 & QUIC Protocol Support: HTTP/3 is built on UDP rather than TCP, meaning packet loss on fluctuating mobile connections does not stall the entire page download. In Kenyan transit environments where users transition from Wi-Fi to cellular data, HTTP/3 provides zero-round-trip session resumption.",
      "Automated Security with Imunify360 & Daily Cloud Backups: High performance without security is reckless. Our LiteSpeed hosting stack includes Imunify360 AI-powered intrusion prevention, automatic brute-force WordPress protection, free Wildcard SSL certificates, and daily off-site cloud snapshots.",
      "The Economic Bottom Line: Upgrading to managed LiteSpeed NVMe hosting from KSh 3,875/year pays for itself within the first week by converting visitors who would otherwise bounce due to sluggish load times."
    ]
  },
  {
    id: "migrating-excel-to-custom-erp",
    slug: "how-to-migrate-complex-excel-workbooks-to-custom-web-apps",
    kicker: "Custom Software",
    title: "How to Safely Migrate Complex Excel Workbooks into Custom Web Apps",
    excerpt: "A tactical blueprint for Kenyan business owners and MDs to escape spreadsheet gridlock, formula corruption, and manual data re-entry.",
    category: "Custom ERPs",
    readTime: "8 min read",
    wordCount: 850,
    date: "August 2026",
    author: "Thinkers Arcade Engineering",
    featuredImage: "/images/custom_erp_dashboard_1790951335673.webp",
    seoKeywords: ["custom ERP Kenya", "convert Excel to app Nairobi", "software development Kenya", "business automation systems", "M-Pesa ERP integration"],
    content: [
      "Nearly every successful Kenyan business begins on Microsoft Excel or Google Sheets. In the early stages, spreadsheets are unbeatable: they are flexible, free, and require zero programming knowledge. You track inventory on one tab, sales on another, staff commissions on a third, and fuel mileage on a fourth.",
      "The Inflection Point: When Spreadsheets Become Operational Hazards: Once your enterprise scales beyond 5 employees, 2 branches, or 100 daily transactions, Excel ceases to be an asset and becomes your biggest point of failure. Consider the typical failure modes in Nairobi businesses today: multiple staff open a shared sheet on Google Drive, leading to overwritten cells; someone accidentally sorts columns A through F while leaving column G intact, permanently corrupting inventory; disgruntled employees copy sensitive supplier price lists and salary columns onto a personal flash drive; and cashiers spend 2 to 3 hours every evening manually cross-referencing M-Pesa transaction codes from their phones into rows.",
      "The 4-Step Migration Blueprint from Thinkers Arcade:",
      "Step 1: Relational Schema Normalization: Spreadsheets flatten multidimensional data into two-dimensional grids. We take your messy multi-tab workbook and extract clean relational tables (e.g. Customers, Inventory Items, Warehouses, Invoices, Transactions) inside a robust PostgreSQL database with strict foreign keys and automated audit logs.",
      "Step 2: Role-Based Access Control (RBAC): In a custom web ERP, cashiers only see the point-of-sale screen; warehouse storekeepers only see quantity counts without knowing supplier cost margins; drivers only see their assigned trip manifests; and the Managing Director accesses an executive KPI dashboard with real-time gross profit figures.",
      "Step 3: Direct API Automation (Daraja M-Pesa & KRA eTIMS): Instead of manual data entry, the custom web app hooks directly into Safaricom's Daraja C2B/B2C API. When a customer sends funds to your Paybill (e.g. Paybill 542542, Account 01550), the system validates the code in 200ms, marks the invoice paid, sends an automated WhatsApp receipt to the client, and transmits the tax record directly to KRA's eTIMS portal.",
      "Step 4: Mobile-First Responsive PWA: Business in East Africa happens on the move. Our web applications are designed as Progressive Web Apps (PWAs) that run effortlessly on smartphones, tablets, or barcode-scanning POS terminals. Warehouse managers can use their phone camera to scan QR barcodes on pallets, updating stock in real time across all branches.",
      "Timeline & Investment: Converting an existing operational spreadsheet into a functioning production web application typically requires just 2 to 4 weeks, with zero recurring per-user licensing fees."
    ]
  },
  {
    id: "solar-ecommerce-shantis-designs",
    slug: "building-solar-clean-energy-ecommerce-platforms-in-kenya",
    kicker: "Solar Clean Tech",
    title: "Building Solar & Clean Energy E-Commerce in Kenya: The Shantis Designs Blueprint",
    excerpt: "How specialized load calculators, technical product catalogs, and instant M-Pesa checkouts transformed Shantis Designs into a leading clean tech retailer.",
    category: "Solar Tech",
    readTime: "7 min read",
    wordCount: 840,
    date: "September 2026",
    author: "Thinkers Arcade E-Commerce Architecture",
    featuredImage: "/images/shantis_solar_ecommerce_1790951316846.webp",
    seoKeywords: ["solar e-commerce Kenya", "solar panels price Nairobi", "solar installation website Kenya", "Shantis Designs solar", "clean energy software Kenya"],
    content: [
      "Kenya is experiencing a historic surge in solar energy adoption. Spurred by volatile electricity tariffs, frequent national grid blackouts, and the dropping cost of Tier-1 lithium batteries, thousands of homeowners, agricultural estates in Nakuru, and manufacturing plants in Industrial Area are seeking solar installations.",
      "The Unique Challenges of Selling Solar Equipment Online: E-commerce for solar and renewable energy is fundamentally different from selling fashion or electronics. Solar products are high-ticket, highly technical investments. A buyer cannot simply 'add to cart' without knowing whether a 5kW hybrid inverter is compatible with a 48V 100Ah lithium iron phosphate (LiFePO4) battery, or how many 550W monocrystalline solar panels are required to power their borehole pump or cold room.",
      "The Shantis Designs E-Commerce Architecture: When Shantis Designs partnered with Thinkers Arcade, our objective was clear: transform an inquiry-only solar catalog into a high-converting digital storefront that educates buyers, automates system sizing, and captures direct payments via M-Pesa and corporate invoicing.",
      "1. The Interactive Solar Sizing & kW Load Calculator: We engineered an intelligent browser-based sizing calculator. Homeowners and commercial facility managers select their daily appliances (refrigerators, water pumps, lighting, air conditioning, machinery). The engine calculates the total daily watt-hour consumption, recommends the exact inverter capacity, specifies the required battery storage hours, and instantly bundles the compatible components into a 1-click cart quotation.",
      "2. Frictionless Daraja M-Pesa & Corporate Bank Payments: For retail solar accessories, cables, charge controllers, and portable solar floodlights, customers check out in seconds using Lipa na M-Pesa STK push. For large commercial installations exceeding KSh 500,000, the system generates an official proforma invoice with automated bank transfer instructions (including our direct I&M Bank Paybill 542542, Account 01550).",
      "3. Technician Dispatch & Site Audit Scheduling: Solar equipment requires certified EPRA-licensed installation. When an order includes installation services, the platform automatically routes the customer's GPS location and roof specifications to the nearest regional installation technician via WhatsApp CRM integration.",
      "4. SEO Dominance in Renewable Energy Keywords: By optimizing product landing pages for high-intent search terms like 'lithium solar battery price Kenya', 'hybrid solar inverters Nairobi', and 'solar water heating installation', Shantis Designs captures commercial customers at the precise moment they are ready to invest.",
      "The Results: Within six months of launch, Shantis Designs recorded a 340% increase in online inquiries, generated over KSh 14.8M in direct web-driven quotes, and reduced the customer inquiry-to-quote turnaround from 48 hours to under 2 minutes."
    ]
  },
  {
    id: "mobile-app-development-kenya-architecture",
    slug: "custom-mobile-app-development-kenya-flutter-vs-native",
    kicker: "App Development",
    title: "Custom Mobile App Development in Kenya: Flutter vs Native Android for Emerging Markets",
    excerpt: "An engineering comparison for Kenyan startups and enterprise directors in Nairobi, Kiambu, Ruiru, Kahawa West, and Mombasa building resilient, bandwidth-conscious apps.",
    category: "Engineering",
    readTime: "8 min read",
    wordCount: 860,
    date: "October 2026",
    author: "Thinkers Arcade Mobile Systems Group",
    featuredImage: "/images/app_development_showcase_1790953231741.webp",
    seoKeywords: [
      "mobile app development Nairobi",
      "app developers Kenya",
      "Flutter developer Nairobi",
      "Android app development Kiambu Ruiru",
      "M-Pesa STK mobile app",
      "iOS app development Mombasa",
      "Kahawa West mobile software"
    ],
    content: [
      "Building mobile software for the Kenyan and broader East African market requires radically different engineering assumptions than developing for Western urban centers. In hubs like Nairobi CBD, Westlands, and Upper Hill, high-speed 5G and fiber Wi-Fi are common; however, as field workers, retail merchants, or logistics couriers travel through Kiambu, Ruiru, Kahawa West, Nakuru, or along the Northern Corridor towards Mombasa, network availability fluctuates dramatically between high-latency 4G and intermittent 3G.",
      "1. Memory & Hardware Constraints on Local Handsets: The vast majority of smartphone users across Kenya utilize Android devices with 2GB to 4GB of RAM (predominantly Transsion brands such as Tecno, Infinix, and itel, alongside budget Samsung Galaxy series). If your mobile application relies on heavy unoptimized JavaScript bridges or bloats beyond 45MB in download size, users will routinely uninstall it when device storage warnings appear. At Thinkers Arcade, our engineering group compiles high-performance Flutter or native Kotlin binaries with aggressive asset compression, guaranteeing initial download payloads below 18MB and 60fps rendering even on entry-tier chipsets.",
      "2. Offline-First Architecture & Background Sync: A mission-critical enterprise app in Kenya cannot assume constant connectivity. Whether an agronomist is recording tea yields in Meru, a field salesperson is taking orders in Ruiru wholesale markets, or a dispatch rider is navigating deliveries in Mombasa, the app must operate 100% offline. We implement local SQLite / Drift embedded databases that store all user entries locally with cryptographic timestamps, and automatically reconcile state via bi-directional background sync the moment Safaricom or Airtel connectivity resumes.",
      "3. Frictionless In-App Daraja M-Pesa STK Integration: Checkout drop-offs in Kenyan mobile apps are almost always caused by clumsy payment redirects. Our mobile team implements direct Safaricom Daraja STK push (and C2B callbacks) directly within the native app screen. Customers enter their Safaricom phone number, receive the native SIM Toolkit PIN prompt immediately over their screen, and upon entering their PIN, the app automatically transitions to a verified green payment confirmation screen in under 3 seconds without ever requiring the user to switch apps or copy reference numbers.",
      "4. Biometric Security & Device-Level Tokenization: Security is paramount for commercial Kenyan mobile platforms handling customer funds or proprietary inventory. We integrate native Android BiometricPrompt and Apple FaceID APIs with hardware-backed Keystore tokenization, ensuring session credentials are encrypted in local secure enclaves rather than plaintext shared preferences.",
      "Conclusion for Product Leaders: Whether your enterprise is based in Nairobi, Thika, or Mombasa, building a custom mobile app provides direct customer retention and field operational telemetry that responsive websites simply cannot match. Thinkers Arcade delivers turnkey mobile architectures from UX wireframing to Google Play and Apple App Store production rollout in 4 to 8 weeks."
    ]
  },
  {
    id: "b2b-distribution-software-kenya",
    slug: "modernizing-wholesale-distribution-software-nairobi-thika-nakuru-meru",
    kicker: "Enterprise ERP",
    title: "Wholesale Distribution & Route-to-Market Software in Nairobi, Thika, Meru & Nakuru",
    excerpt: "How FMCG suppliers, hardware wholesalers, and agro-dealers across Thika Road, Meru, and Rift Valley eliminate van inventory shrinkage with real-time field apps.",
    category: "Custom ERPs",
    readTime: "8 min read",
    wordCount: 890,
    date: "October 2026",
    author: "Thinkers Arcade Supply Chain Engineering",
    featuredImage: "/images/store_pos_hardware_1790951352250.webp",
    seoKeywords: [
      "wholesale distribution software Kenya",
      "van sales app Nairobi",
      "FMCG software Thika Nakuru Meru",
      "Lipa na M-Pesa Paybill software",
      "warehouse ERP Kenya",
      "Kiambu distribution app",
      "inventory tracking Ruiru"
    ],
    content: [
      "Wholesale commerce and route-to-market distribution form the backbone of Kenya's non-agricultural GDP. Thousands of medium and large distribution businesses operate along the industrial arteries of Nairobi, the bustling manufacturing and warehousing belts of Thika Road, Ruiru, and industrial estates in Nakuru and Meru. These companies move millions of shillings daily in hardware supplies, solar equipment, fast-moving consumer goods (FMCG), pharmaceuticals, and agricultural inputs to tens of thousands of retail dukas.",
      "The Chronic Cost of Analog Paper Route Sheets: Despite managing multi-million shilling daily stock movements, a staggering percentage of Kenyan distributors still rely on carbon-copy paper delivery manifests, manual driver trip books, and post-dated physical cheques. By the time distribution trucks return to depots in Industrial Area, Ruiru, or Thika at 7:00 PM, accounting clerks spend upwards of 3 hours manually cross-checking physical delivery notes against cash envelopes and individual M-Pesa SMS messages on drivers' personal phones.",
      "1. Van Sales & Real-Time Truck Inventory Management: Thinkers Arcade engineers specialized mobile field sales applications mounted on rugged Android tablets or drivers' smartphones. When a truck departs the central warehouse in Nairobi or Thika, its morning inventory is scanned via 2D Bluetooth barcode reader into the app. As delivery agents visit retailers in Kiambu, Kahawa West, or Ruiru, each sale deducts stock from the van's rolling balance in real time.",
      "2. Automated Payment Reconciliation via Direct Paybill (542542 / 01550): Driver cash collection poses severe security vulnerabilities and reconciliation delays. Our software replaces informal driver Till numbers with centralized, automated Lipa na M-Pesa Paybill integration (such as Paybill 542542, Account 01550). When a retailer in Nakuru or Meru pays for a shipment, the invoice number is encoded into the payment account string. The transaction is verified in milliseconds on the driver's screen, and an e-receipt is instantly generated via Bluetooth thermal printer or WhatsApp.",
      "3. Automated KRA eTIMS Fiscalization on Delivery: Under current Kenyan tax regulations, commercial transactions must be accompanied by a KRA eTIMS valid QR code at the point of sale. Our systems interface directly with the KRA Virtual Sales Control Unit (VSCU) via API, generating legal fiscal invoices with valid QR codes immediately upon transaction completion, eliminating end-of-month tax compliance penalties.",
      "4. GPS Route Geofencing & Delivery Proof: To eliminate phantom stops and unauthorized route deviations along highway corridors between Nairobi, Thika, and Meru, the application logs GPS coordinates at every delivery stop, capturing digital customer signatures and photo proof of offloaded stock.",
      "Summary: By modernizing distribution workflows with custom Thinkers Arcade enterprise portals, Kenyan wholesale suppliers routinely eliminate 98% of invoice reconciliation discrepancies, slash daily driver book-in times from 3 hours to 15 minutes, and eliminate inventory theft."
    ]
  },
  {
    id: "kenya-cybersecurity-etims-compliance",
    slug: "cybersecurity-etims-api-data-protection-compliance-kenya",
    kicker: "Cybersecurity & Compliance",
    title: "Cybersecurity, KRA eTIMS API Hardening & Data Protection Compliance for Kenyan Enterprises",
    excerpt: "A technical security guide for commercial firms in Nairobi, Nakuru, Thika, Meru, and Mombasa to prevent ransomware, secure API integrations, and comply with the Kenya Data Protection Act.",
    category: "Security",
    readTime: "8 min read",
    wordCount: 880,
    date: "October 2026",
    author: "Thinkers Arcade Security & Cloud Operations",
    featuredImage: "/images/cloud_enterprise_servers_1790953244864.webp",
    seoKeywords: [
      "cybersecurity services Nairobi",
      "KRA eTIMS API security Kenya",
      "Kenya Data Protection Act ODPC compliance",
      "penetration testing Thika Nakuru",
      "secure cloud hosting Mombasa",
      "network security Kiambu Ruiru Kahawa West"
    ],
    content: [
      "As Kenyan enterprises accelerate their migration to digital platforms, cloud hosting, and integrated ERP systems, cybersecurity has transformed from a back-office IT consideration into an existential board-level responsibility. Across commercial hubs from Nairobi's Upper Hill and Westlands financial districts to the manufacturing complexes of Thika and Ruiru, companies face unprecedented attack surfaces. The combination of mandatory KRA eTIMS API integrations, high-volume Lipa na M-Pesa merchant endpoints, and remote branch workers in Nakuru, Meru, and Mombasa creates high-value targets for ransomware operators and cyber extortion syndicates.",
      "1. The Regulatory Imperative: Kenya Data Protection Act (ODPC) Hardening: Under the Kenya Data Protection Act (2019) enforced by the Office of the Data Protection Commissioner (ODPC), any organization collecting personal identification, phone numbers, or transactional history from Kenyan citizens must implement technical controls safeguarding that data against unauthorized processing or exfiltration. Non-compliance risks statutory penalties of up to KSh 5,000,000 or 1% of annual gross turnover. Thinkers Arcade implements automated cryptographic data tokenization at rest (AES-256) and TLS 1.3 in transit, ensuring customer PII is never stored in unencrypted relational database columns.",
      "2. Securing KRA eTIMS & Safaricom Daraja API Endpoints: Modern commercial applications communicate continuously with external statutory and financial APIs. A critical vulnerability frequently uncovered during our security audits is the hardcoding of KRA eTIMS OSCU/VSCU client certificates or Safaricom Daraja consumer secrets within front-end JavaScript bundles or insecure environment files. Our security engineering team establishes hardware-isolated secrets vaults, server-side reverse proxies, and mutual TLS (mTLS) handshakes. We implement IP address whitelisting so that only authorized server nodes peering directly at KIXP (Kenya Internet Exchange Point) can trigger tax invoice validation or M-Pesa STK callbacks.",
      "3. Mitigating Distributed Denial of Service (DDoS) & Web Application Attacks: Sluggish website performance during commercial promotional campaigns is often not caused by high traffic alone, but by aggressive scraping bots or Layer-7 HTTP flood attacks. Standard hosting packages lack the computational headroom to scrub malicious traffic. Our managed infrastructure integrates enterprise-grade Imunify360 web application firewalls (WAF) and multi-terabit edge scrubbing. When malicious botnets attempt credential stuffing against customer login portals in Kiambu or Kahawa West, our real-time behavioral analysis drops the attack packets in sub-milliseconds without degrading legitimate user checkout flows.",
      "4. Penetration Testing & Vulnerability Assessment Workflows: Scheduled vulnerability testing is no longer optional for Kenyan businesses handling electronic payments. Thinkers Arcade conducts rigorous black-box and white-box penetration testing modeled on the OWASP Top 10. We audit SQL injection vectors, cross-site scripting (XSS), insecure direct object references (IDOR), and privilege escalation paths across custom web portals and mobile Flutter apps before production deployment.",
      "Conclusion & Security Roadmap: Security is an ongoing operational posture, not a one-time sticker. By partnering with Thinkers Arcade, forward-thinking enterprises across Nairobi, Mombasa, Nakuru, Meru, and Thika protect their brand reputation, guarantee 99.99% system uptime, and achieve effortless statutory compliance."
    ]
  },
  {
    id: "hyper-local-seo-kenya-regional-playbook",
    slug: "hyper-local-seo-google-maps-ranking-nairobi-nakuru-thika-meru-mombasa-kiambu-ruiru-kahawa-west",
    kicker: "Local Search Domination",
    title: "The 2026 Hyper-Local SEO Playbook: Dominating Google Maps Across Nairobi, Nakuru, Thika, Meru, Mombasa, Kiambu, Ruiru & Kahawa West",
    excerpt: "How Kenyan service providers and regional retailers capture high-converting search intent across Kenya's fastest-growing municipal business corridors.",
    category: "SEO",
    readTime: "9 min read",
    wordCount: 915,
    date: "October 2026",
    author: "Thinkers Arcade Search Engineering Group",
    featuredImage: "/images/strategic_founders_advisory_1790953255396.webp",
    seoKeywords: [
      "SEO services Nairobi Kenya",
      "best SEO company Nakuru",
      "Google Maps ranking Thika",
      "local business SEO Meru",
      "website SEO services Mombasa",
      "SEO agency Kiambu",
      "local search optimization Ruiru",
      "Kahawa West digital marketing"
    ],
    content: [
      "Over the past three years, the geographic distribution of high-value search queries in Kenya has decentralized dramatically. While Nairobi previously accounted for nearly 75% of high-intent commercial web searches, rapid suburbanization and regional economic growth have created surging search clusters along the Thika Superhighway (Kiambu Town, Ruiru, Kahawa West, Juja, Thika), across agricultural and industrial nodes in Nakuru and Meru, and throughout the coastal trade hub of Mombasa. Commercial buyers in these areas no longer search with generic keywords; they search with immediate local intent such as 'commercial solar installers in Ruiru', 'custom ERP software developers Thika Road', or 'fast website hosting near Kahawa West'.",
      "1. Programmatic Sub-Market Landing Page Architecture: Most Kenyan agencies attempt to rank for every town using a single generic contact page that simply lists names of cities in small print. Google's algorithmic spam and quality filters instantly devalue this approach. To dominate local rankings, Thinkers Arcade develops dedicated, content-rich programmatic landing pages for each target node: Nairobi CBD, Westlands, Kiambu, Ruiru, Kahawa West, Thika, Nakuru, Meru, and Mombasa. Each page features authentic hyper-local content: localized driving directions, proximity to known landmarks (such as Ruiru Toll Station, Thika Section 9, Meru Makutano, or Nakuru West), localized case studies, and explicit customer review embeds from verified local clients.",
      "2. Google Business Profile Multi-Location Entity Clustering: Google Maps evaluates three primary ranking pillars: Proximity, Prominence, and Relevance. To maximize prominence without violating Google's strict guidelines against phantom addresses, we optimize your Google Business Profile with secondary service areas, rich high-resolution photo assets with authentic EXIF metadata, and automated review acquisition sequences. When customers leave reviews mentioning specific geographic phrases ('helped our hardware store in Ruiru' or 'installed our solar system in Nakuru'), Google's entity graph assigns localized ranking weight directly to your listing.",
      "3. Fast Local Mobile Hosting & Edge CDN Caching: In areas like Kahawa West, Ruiru, and Meru, users frequently browse on varying 4G mobile signal strengths. A website that takes 4 seconds to load loses 60% of search visitors before the first image renders. By pairing our technical SEO architecture with NVMe LiteSpeed cloud hosting peered directly at the Kenya Internet Exchange Point (KIXP), pages achieve a First Contentful Paint (FCP) of under 0.6 seconds on Kenyan mobile networks, securing an immediate technical ranking edge over competitors on foreign Apache servers.",
      "4. Schema.org Multi-Location Graph Markup: We embed structured LocalBusiness JSON-LD markup containing precise GPS latitude and longitude coordinates, geo-radius delivery polygons, opening hours, local Safaricom M-Pesa Paybill payment acceptance (Paybill 542542, Account 01550), and telephone contact endpoints (+254 114646341). This allows search crawlers and AI search agents (including Google Gemini and ChatGPT) to ground business facts with zero hallucination.",
      "Action Plan: Transitioning your business from invisible to omnipresent on Google across Kenya's key commercial municipalities requires systematic execution. A targeted 90-day search rollout with Thinkers Arcade establishes permanent topical authority that compounds month after month."
    ]
  }
];
export const STORE_PRODUCTS: StoreProduct[] = [
  {
    id: "delwhite-complete-retail-pos-package",
    name: "DelWhite Complete Retail POS Package",
    category: "Turnkey POS Solution",
    priceKes: 35000,
    badge: "Best Seller",
    description: "Full hardware kit and software licence for supermarkets, mini-marts and retail outlets in Kenya.",
    specifications: [
      "Lifetime DelWhite POS Software Licence (Zero monthly fees)",
      "Heavy-duty 80mm High-Speed Thermal Receipt Printer (USB & LAN)",
      "Omnidirectional 2D & QR Barcode Scanner (Reads phone screens)",
      "Reinforced 5-bill / 8-coin Steel Cash Drawer with RJ11 auto-trigger",
      "Free onsite delivery, software configuration & staff training across Kenya",
      "Direct integration with M-Pesa Paybill / Till Number"
    ],
    inStock: true,
    warranty: "1 Year Full Replacement Warranty",
    leadTime: "Same-day delivery in Nairobi; Next-day countrywide",
    imageUrl: "/images/store_pos_hardware_1790951352250.webp"
  },
  {
    id: "delwhite-software-only-pos-package",
    name: "DelWhite Software-Only POS Package",
    category: "Retail Software",
    priceKes: 20000,
    description: "Ideal for businesses that already own compatible desktop PCs, laptops and thermal printers.",
    specifications: [
      "Single-station permanent licence key with offline sync capability",
      "Inventory tracking, low-stock warnings & expiry date monitoring",
      "Multi-payment support (Cash, M-Pesa, Card, Invoice Credit)",
      "Daily Z-Report and X-Report sales analytics sent to owner WhatsApp",
      "Compatible with Windows 10, Windows 11 & Linux",
      "Free remote installation via AnyDesk / TeamViewer"
    ],
    inStock: true,
    warranty: "Lifetime Software Updates & Remote Support",
    leadTime: "Instant License Activation within 30 Minutes",
    imageUrl: "/images/custom_erp_dashboard_1790951335673.webp"
  },
  {
    id: "fiscal-kra-tims-compliance",
    name: "Fiscal & KRA TIMS Compliance Solutions",
    category: "Fiscal & Tax",
    priceKes: 45000,
    badge: "KRA Approved",
    description: "Approved electronic tax registers and fiscal signature devices synced directly to KRA systems.",
    specifications: [
      "KRA TIMS Type A, Type B and Type C certified hardware",
      "Electronic Signature Device (ESD) for direct ERP / invoicing integration",
      "Automated transmission of QR codes on every customer invoice",
      "Internal cryptographic memory storing 5+ years of audited sales",
      "Includes KRA portal serial mapping, registration & tax calibration",
      "Compliant with Kenyan Data Protection Act & Finance Act regulations"
    ],
    inStock: true,
    warranty: "2 Years Manufacturer Warranty",
    leadTime: "24-48 Hours with KRA Registration",
    imageUrl: "/images/store_pos_hardware_1790951352250.webp"
  },
  {
    id: "smart-retail-fnb-software-suite",
    name: "Smart Retail & F&B Software Suite",
    category: "Retail & F&B",
    priceKes: 55000,
    description: "Enterprise software for supermarkets, restaurants, QSRs and food ordering platforms.",
    specifications: [
      "Table management, kitchen order tickets (KOT) and recipe cost tracking",
      "Wait-staff Android / iOS mobile ordering integration",
      "Multi-store inventory transfers and centralized warehouse management",
      "Loyalty rewards program with customer SMS balance updates",
      "Real-time profit margin analysis per dish / per SKU"
    ],
    inStock: true,
    warranty: "1 Year SLA & 24/7 Hotline Support",
    leadTime: "Deployment & Training within 3 Business Days",
    imageUrl: "/images/app_development_showcase_1790953231741.webp"
  },
  {
    id: "core-it-hardware-physical-security",
    name: "Core IT Hardware & Physical Security",
    category: "Hardware & Security",
    priceKes: 120000,
    description: "Integrated POS hardware, biometric access control and IP CCTV surveillance networks.",
    specifications: [
      "4K IP Night-Vision Surveillance Cameras with remote phone viewing",
      "Biometric fingerprint & facial recognition staff time-and-attendance clock",
      "Network video recorder (NVR) with 4TB continuous recording storage",
      "Structured CAT6 shielded cabling and surge protection rackmount cabinet",
      "Centralized monitoring dashboard for loss prevention"
    ],
    inStock: true,
    warranty: "1 Year Onsite Equipment Warranty",
    leadTime: "Scheduled Onsite Installation (Nairobi & Countrywide)",
    imageUrl: "/images/cloud_enterprise_servers_1790953244864.webp"
  },
  {
    id: "infrastructure-technical-services",
    name: "Infrastructure & Technical Services",
    category: "Managed IT",
    priceKes: 150000,
    badge: "Enterprise SLA",
    description: "Turnkey IT infrastructure design, cloud migration and ongoing maintenance retainers.",
    specifications: [
      "Full server room cabling, firewall deployment & WAN load balancing",
      "Zero-downtime database migration to NVMe LiteSpeed VPS clusters",
      "Quarterly onsite hardware maintenance & preventive dust cleansing",
      "Dedicated senior network & systems engineer on WhatsApp SLA",
      "Automated offsite daily cloud disaster recovery backups"
    ],
    inStock: true,
    warranty: "Enterprise Guaranteed 99.99% Uptime SLA",
    leadTime: "Initial Audit within 24 Hours",
    imageUrl: "/images/cloud_enterprise_servers_1790953244864.webp"
  }
];

export const STORE_FAQS = [
  {
    q: "How much does a complete POS system cost in Kenya?",
    a: "The DelWhite Complete Retail POS Package costs KSh 35,000 and includes the lifetime POS software licence, an 80mm thermal receipt printer, a 2D/QR barcode scanner, a steel cash drawer, onsite setup and staff training. A software-only licence is KSh 20,000 one-time with zero recurring fees."
  },
  {
    q: "How can I pay directly via M-Pesa Paybill?",
    a: "You can pay directly via Lipa na M-Pesa Paybill Number: 542542, Account Number: 01550 (I&M Bank - Hosting Account). Once paid, input your M-Pesa transaction reference in our checkout or WhatsApp us at +254 114 646 341 for immediate dispatch."
  },
  {
    q: "Are your fiscal devices KRA TIMS approved?",
    a: "Yes. We supply and integrate KRA TIMS Type A, B and C approved units, Electronic Signature Devices (ESD), fiscal printers and mobile Bluetooth tax terminals, including KRA portal mapping and onsite calibration, starting from KSh 45,000."
  },
  {
    q: "Do you deliver and install outside Nairobi?",
    a: "Yes. Standard installation covers Nairobi, and we run regular regional deployment trips to Mombasa, Kisumu, Nakuru, Eldoret, Meru, Thika, Nyeri, and Machakos, plus cross-border logistics to Uganda, Tanzania, and Rwanda."
  },
  {
    q: "Is annual maintenance compulsory?",
    a: "No. Every bundle ships with a comprehensive warranty and complete handover training. Annual maintenance contracts are optional and start at KSh 6,000/year for software-only and KSh 12,000/year for complete physical hardware kits."
  },
  {
    q: "Can you integrate the POS with M-Pesa, accounting and e-commerce?",
    a: "Yes! We integrate M-Pesa Paybill and Till Number payments with automatic STK push, accounting tools like QuickBooks, Sage, or Zoho, and online storefronts (WooCommerce/Next.js) so stock and sales remain synchronized across channels."
  }
];

export const KENYAN_COMMERCIAL_KEYWORDS = [
  { keyword: "SEO Services Company Nairobi Kenya", searchVolume: "3,600/mo", intent: "Commercial Transactional", cpc: "KSh 185", category: "SEO" },
  { keyword: "Best Web Development Companies in Kenya", searchVolume: "2,900/mo", intent: "Commercial High-Intent", cpc: "KSh 210", category: "Web Development" },
  { keyword: "NVMe LiteSpeed Web Hosting Kenya", searchVolume: "4,400/mo", intent: "Transactional Buying", cpc: "KSh 140", category: "Cloud Hosting" },
  { keyword: "Retail POS System Price in Kenya", searchVolume: "5,100/mo", intent: "Commercial Buyer", cpc: "KSh 160", category: "Store & POS" },
  { keyword: "Custom ERP Software Development Nairobi", searchVolume: "1,800/mo", intent: "Enterprise Decision-Maker", cpc: "KSh 320", category: "Custom ERPs" },
  { keyword: "Solar Installation & Panels E-Commerce Kenya", searchVolume: "3,200/mo", intent: "Commercial Buyer", cpc: "KSh 190", category: "Solar Tech" },
  { keyword: "KRA TIMS Approved Fiscal Devices Nairobi", searchVolume: "2,200/mo", intent: "Regulatory Purchasing", cpc: "KSh 240", category: "Fiscal Compliance" },
  { keyword: ".co.ke Domain Registration Nairobi", searchVolume: "6,800/mo", intent: "Transactional Direct", cpc: "KSh 95", category: "Domains" }
];


export const STATS_HIGHLIGHTS = [
  { value: "300%", label: "Lead growth", sub: "Average cross-client pipeline acceleration" },
  { value: "250%", label: "Organic traffic growth", sub: "Compounding non-brand organic search reach" },
  { value: "500%", label: "Engagement growth", sub: "Interactive UX and dwell-time uplift" },
  { value: "98%", label: "Client satisfaction", sub: "Retention and NPS across all regional contracts" },
];

export const CREDIBILITY_BADGES = [
  { label: "Enterprise-grade governance", sub: "Zero-compromise security posture" },
  { label: "Live across 12+ regions", sub: "Continental and global client footprint" },
  { label: "Built for Pan-African scale", sub: "High throughput and localized rails" },
  { label: "24/7 Engineering support", sub: "Observability and continuous uptime" },
];

export const GROWTH_PILLARS: Pillar[] = [
  {
    id: "infrastructure",
    number: "01 · Pillar",
    title: "Infrastructure",
    subtitle: "The platforms ambitious businesses are built on.",
    tagline: "High-performance digital surfaces built on modern web stacks with sub-second speeds and bulletproof uptime.",
    items: [
      {
        name: "Website Platforms",
        description: "Custom-engineered marketing and corporate surfaces optimized for lightning-fast Core Web Vitals and conversions.",
        features: ["Sub-second page speeds", "Responsive architecture", "Custom CMS integration", "Automated backup pipelines"],
        link: "/solutions/website-development",
      },
      {
        name: "App Development",
        description: "High-performance cross-platform iOS & Android mobile applications, Progressive Web Apps (PWAs), and offline-first field operations apps.",
        features: ["Native iOS & Android performance", "Offline SQLite / IndexedDB sync", "Push notifications & biometric login", "M-Pesa STK in-app checkout"],
        link: "/solutions/app-development",
      },
      {
        name: "Software Systems",
        description: "Bespoke web applications, customer portals, billing backends, and internal workflow management systems.",
        features: ["Multi-tenant architecture", "Role-based access control", "High-throughput API endpoints", "PostgreSQL / Cloud scale"],
        link: "/solutions/software-development",
      },
      {
        name: "Commerce Infrastructure",
        description: "Transaction engines with seamless M-Pesa STK push, card gateways, inventory sync, and multi-currency checkouts.",
        features: ["Direct Daraja M-Pesa API", "Real-time stock syncing", "Abandoned cart recovery", "Automated receipting"],
        link: "/solutions/e-commerce",
      },
    ],
  },
  {
    id: "intelligence",
    number: "02 · Pillar",
    title: "Intelligence",
    subtitle: "AI, automation and decision systems that compound.",
    tagline: "Turn manual bottlenecks into automated, autonomous pipelines that scale your team 10x without added headcount.",
    items: [
      {
        name: "AI Systems",
        description: "Domain-specific AI assistants, customer intelligence engines, and generative knowledge agents built on modern models.",
        features: ["Enterprise RAG pipelines", "Internal knowledge bases", "Automated lead triage", "Multilingual East African NLP"],
        link: "/solutions/ai-solutions",
      },
      {
        name: "Business Automation",
        description: "End-to-end integration across CRM, WhatsApp bots, invoicing, calendar scheduling, and operational tooling.",
        features: ["WhatsApp Business automation", "Instant CRM lead routing", "Zapier & n8n workflows", "Automated reconciliation"],
        link: "/solutions/business-automation",
      },
      {
        name: "Analytics & Search",
        description: "Unified telemetry dashboards providing deep clarity on user behaviour, attribution, funnel leakage, and ROI.",
        features: ["Server-side tracking", "Conversion funnel analysis", "Executive KPI dashboards", "Attribution modeling"],
        link: "/solutions/seo",
      },
    ],
  },
  {
    id: "growth",
    number: "03 · Pillar",
    title: "Growth",
    subtitle: "Distribution, positioning and demand engines.",
    tagline: "Sustainable customer acquisition channels that reduce reliance on unpredictable ad algorithms.",
    items: [
      {
        name: "Search Infrastructure",
        description: "Technical search engine optimization architected to dominate commercial keywords across Nairobi, Kenya, and global markets.",
        features: ["Entity-based topical clusters", "Programmatic SEO setup", "Core Web Vitals tuning", "Schema.org structured graphs"],
        link: "/solutions/seo",
      },
      {
        name: "Growth Marketing",
        description: "Performance acquisition funnels, retention loops, email sequences, and high-converting paid search campaigns.",
        features: ["High-intent search capture", "Retention lifecycle emails", "B2B LinkedIn outreach", "A/B landing experiments"],
        link: "/solutions/digital-marketing",
      },
      {
        name: "Brand Positioning",
        description: "Narrative architecture, brand identity systems, and authority assets that make enterprise buyers take notice.",
        features: ["Executive pitch decks", "Design system tokens", "Category creation copy", "Brand style guides"],
        link: "/solutions/brand-strategy",
      },
    ],
  },
];

export const OFFICIAL_PRICE_CATALOGUE: PricePackage[] = [
  // Website Packages
  {
    id: "landing-page",
    name: "Landing Page",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 9000,
    priceDisplay: "KES 9,000",
    description: "High-impact single-page conversion website built to turn traffic from Google & social ads into paying customer calls.",
    features: [
      "Custom responsive mobile & desktop design",
      "Direct WhatsApp & phone call-to-action buttons",
      "Interactive lead inquiry / quotation form",
      "Google Maps location & social links integration",
      "Fast 48-hour delivery & deployment"
    ],
    popular: true,
    lipaPolePole: false,
    turnaround: "48 Hours"
  },
  {
    id: "starter-website",
    name: "Starter Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 15000,
    priceDisplay: "KES 15,000",
    description: "Ideal for startups and local businesses wanting a clean, trustworthy web presence with up to 4 core pages.",
    features: [
      "Up to 4 custom pages (Home, About, Services, Contact)",
      "Mobile-optimized with sub-1s fast loading",
      "Free SSL certificate setup & security hardening",
      "Basic on-page SEO for Kenyan search visibility",
      "Free 30-day post-launch support"
    ],
    turnaround: "3 - 4 Days"
  },
  {
    id: "personal-website",
    name: "Personal Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 15000,
    priceDisplay: "KES 15,000",
    description: "Tailored for consultants, executives, lawyers, doctors, politicians, and creators to showcase authority.",
    features: [
      "Executive personal biography & credentials portfolio",
      "Downloadable resume/CV & speaking engagement booking",
      "Articles & thought-leadership blog section",
      "Social media feeds & newsletter subscription",
      "Custom business email setup"
    ],
    turnaround: "3 - 4 Days"
  },
  {
    id: "business-website",
    name: "Business Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 25000,
    priceDisplay: "KES 25,000",
    description: "Professional corporate website designed for established SMEs looking to dominate their local industry.",
    features: [
      "Up to 8 custom pages with modern executive layout",
      "Interactive service catalogs & quote calculators",
      "Local Google Business Profile & SEO optimization",
      "M-Pesa payment integration ready",
      "WhatsApp live chat floating widget"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "5 Days"
  },
  {
    id: "business-pro-website",
    name: "Business Pro Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 30000,
    priceDisplay: "KES 30,000",
    description: "Enhanced business portal with customer lead capture, CRM pipeline integration, and speed tuning.",
    features: [
      "Up to 12 custom pages with premium animations",
      "CRM & email marketing lead pipeline integration",
      "Sub-0.8s Core Web Vitals speed optimization",
      "Multi-location Kenyan municipal SEO markup",
      "Client testimonial & case study showcase"
    ],
    lipaPolePole: true,
    turnaround: "5 - 7 Days"
  },
  {
    id: "travel-booking-website",
    name: "Travel & Booking Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 30000,
    priceDisplay: "KES 30,000",
    description: "Engineered specifically for tour operators, safari agencies, car rentals, and boutique hotels.",
    features: [
      "Tour packages showcase with day-by-day itinerary builders",
      "Direct online reservation & date availability calendar",
      "Multi-currency support (KES, USD, EUR, GBP)",
      "TripAdvisor reviews & traveler photo gallery",
      "Instant WhatsApp booking dispatch"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "5 - 7 Days"
  },
  {
    id: "ecommerce-website",
    name: "Ecommerce Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 35000,
    priceDisplay: "KES 35,000",
    description: "Complete online shop to start selling products countrywide with automated M-Pesa payments.",
    features: [
      "Full product catalog with categories, search & filters",
      "Direct Safaricom M-Pesa Paybill / Till Number checkout",
      "Shopping cart with instant SMS/Email order alerts",
      "Customer accounts & order tracking dashboard",
      "Up to 30 products uploaded with training for your staff"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "5 - 7 Days"
  },
  {
    id: "school-website",
    name: "School Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 35000,
    priceDisplay: "KES 35,000",
    description: "Modern educational institution portal for primary schools, high schools, colleges, and training academies.",
    features: [
      "Online student admission & registration forms",
      "Term fee structure & bank / M-Pesa payment guides",
      "Academic calendar, term dates & circular downloads",
      "School facilities photo gallery & news announcements",
      "Staff directory & departmental contacts"
    ],
    lipaPolePole: true,
    turnaround: "5 - 7 Days"
  },
  {
    id: "shopify-store",
    name: "Shopify Store",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 40000,
    priceDisplay: "KES 40,000",
    description: "World-class turnkey Shopify e-commerce store with Kenyan payment gateway and apps configured.",
    features: [
      "Premium Shopify theme installation & brand styling",
      "Kenyan M-Pesa & Card payment gateway integration",
      "Product variant setup (sizes, colors, stock counts)",
      "Automated abandoned cart email recovery sequences",
      "Staff onboarding on managing products via Shopify mobile app"
    ],
    turnaround: "4 - 6 Days"
  },
  {
    id: "booking-service-website",
    name: "Booking & Service Website",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 40000,
    priceDisplay: "KES 40,000",
    description: "For medical clinics, beauty salons, consulting firms, and fitness centers needing automated scheduling.",
    features: [
      "Real-time appointment booking with Google Calendar sync",
      "Staff rota management & service provider selection",
      "Deposit / full payment via M-Pesa prior to booking",
      "Automated WhatsApp & SMS appointment reminders",
      "Customer client history & booking management dashboard"
    ],
    lipaPolePole: true,
    turnaround: "5 - 7 Days"
  },
  {
    id: "ecommerce-full-suite",
    name: "E-commerce Website (Full Suite)",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 50000,
    priceDisplay: "KES 50,000",
    description: "Enterprise retail supermarket store with unlimited SKUs, automated M-Pesa STK push, and shipping APIs.",
    features: [
      "Unlimited product inventory with multi-warehouse tracking",
      "Instant Safaricom Daraja M-Pesa STK push prompt on mobile",
      "Courier delivery fee calculation (Fargo, Wells, Speedaf, G4S)",
      "Discount coupons, flash sales & customer loyalty points",
      "Detailed financial profit & sales performance analytics"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "7 - 10 Days"
  },
  {
    id: "laravel-systems-web",
    name: "Laravel Systems",
    category: "websites",
    categoryLabel: "Website Packages",
    priceKes: 65000,
    priceDisplay: "KES 65,000",
    description: "Custom-coded Laravel web platform with proprietary database architecture and complex business logic.",
    features: [
      "Tailor-made Laravel & Vue/React modern architecture",
      "Multi-role user permission hierarchies (Admin, Staff, Client)",
      "High-throughput transactional database (PostgreSQL/MySQL)",
      "Full API integrations (SMS gateways, M-Pesa, KRA eTIMS)",
      "Guaranteed source code handover & technical documentation"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "10 - 14 Days"
  },

  // Digital Systems & Organizations
  {
    id: "school-education-systems",
    name: "School & Education Systems",
    category: "systems",
    categoryLabel: "Digital Systems & Organizations",
    priceKes: 35000,
    priceDisplay: "Starting at KES 35,000",
    isStartingAt: true,
    description: "Complete school management software for student grading, report cards, fees, and parent communication.",
    features: [
      "CBC & 8-4-4 grading system with automated report card generation",
      "Student fee ledger with M-Pesa receipting & balance tracking",
      "Bulk SMS alerts to parents for fee reminders & school notices",
      "Teacher attendance, timetable generator & lesson plans",
      "Onsite installation and teacher/bursar training"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "7 - 10 Days"
  },
  {
    id: "ngo-church-systems",
    name: "NGO & Church Systems",
    category: "systems",
    categoryLabel: "Digital Systems & Organizations",
    priceKes: 45000,
    priceDisplay: "Starting at KES 45,000",
    isStartingAt: true,
    description: "Specialized organizational management software for congregations, non-profits, and charitable trusts.",
    features: [
      "Member directory & family fellowship cell group tracking",
      "Tithe, offering & project donation recording via M-Pesa",
      "Departmental volunteer scheduling & event check-ins",
      "Donor financial reports & project fund accounting",
      "Automated birthday & anniversary SMS greetings to members"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "7 - 10 Days"
  },
  {
    id: "custom-laravel-systems",
    name: "Custom Laravel Systems",
    category: "systems",
    categoryLabel: "Digital Systems & Organizations",
    priceKes: 45000,
    priceDisplay: "Starting at KES 45,000",
    isStartingAt: true,
    description: "Bespoke internal software replacing cluttered Excel spreadsheets with secure, cloud-accessible portals.",
    features: [
      "Spreadsheet-to-web migration with zero data loss",
      "Multi-branch inventory management & stock transfers",
      "KRA eTIMS automated tax invoice synchronization",
      "Staff role-based access control & complete audit trail logs",
      "Accessible securely from any smartphone, tablet, or desktop"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "10 - 14 Days"
  },

  // Design Services
  {
    id: "woocommerce-products-upload",
    name: "WooCommerce Products Upload & SEO",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 200,
    priceDisplay: "KES 200",
    pricePeriod: "per product",
    description: "Meticulous e-commerce product catalog upload with clean images, descriptions, and Google SEO tags.",
    features: [
      "High-resolution image editing & background cleaning",
      "Persuasive commercial product description writing",
      "SKU, stock count, and price configuration",
      "Google Shopping & search keyword meta tagging",
      "Fast bulk turnaround"
    ],
    turnaround: "24 Hours"
  },
  {
    id: "poster-design",
    name: "Poster Design",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 800,
    priceDisplay: "KES 800",
    description: "Striking promotional graphic posters crafted for WhatsApp status, Facebook, Instagram, and printing.",
    features: [
      "Custom branded artwork tailored to your campaign",
      "Optimized dimensions for WhatsApp Status & Instagram",
      "High-resolution print-ready PDF and web JPG/PNG",
      "Up to 2 revisions included",
      "Same-day 24-hour turnaround"
    ],
    popular: true,
    turnaround: "24 Hours"
  },
  {
    id: "certificate-design",
    name: "CERTIFICATE DESIGN",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 1500,
    priceDisplay: "KES 1,500",
    description: "Prestigious certificate layouts for graduation, corporate workshops, sports awards, and fellowships.",
    features: [
      "High-elegance guilloche borders and official seals",
      "Placement for verification QR codes & serial numbers",
      "Editable name fields template for bulk student printing",
      "Vector CMYK print-ready layout",
      "Quick 24-hour turnaround"
    ],
    turnaround: "24 Hours"
  },
  {
    id: "id-card-design",
    name: "ID CARD DESIGN",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 2000,
    priceDisplay: "KES 2,000",
    description: "Professional corporate employee, school student, or event visitor PVC identification cards.",
    features: [
      "Front and back standard CR80 PVC card layouts",
      "Barcode, QR code, and photo placeholder alignment",
      "Corporate brand colors, logo, and terms & conditions",
      "High-DPI print-ready files compatible with card printers",
      "24-hour turnaround"
    ],
    turnaround: "24 Hours"
  },
  {
    id: "email-signature",
    name: "Email Signature",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 2000,
    priceDisplay: "KES 2,000",
    description: "Clickable HTML email footer that makes every email your company sends look professional.",
    features: [
      "Clickable direct WhatsApp, telephone, and website links",
      "Embedded corporate logo and staff headshot",
      "Social media icon buttons (LinkedIn, Facebook, X)",
      "Tested for 100% compatibility in Gmail, Outlook & Apple Mail",
      "Installation instructions provided"
    ],
    turnaround: "24 Hours"
  },
  {
    id: "logo-design",
    name: "Logo Design",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 3500,
    priceDisplay: "KES 3,500",
    description: "Memorable corporate brand identity designed to make your business look like an established market leader.",
    features: [
      "3 distinct original creative design concepts",
      "Unlimited revisions on your chosen concept",
      "High-resolution vector files (SVG, PDF, EPS, PNG transparent)",
      "Favicon format for websites and social profile icons",
      "Color codes (HEX, RGB, CMYK) and font guide"
    ],
    popular: true,
    turnaround: "48 Hours"
  },
  {
    id: "company-profile",
    name: "Company Profile",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 4000,
    priceDisplay: "KES 4,000",
    description: "Executive 8 to 12-page corporate brochure designed to win tenders, bank financing, and high-value contracts.",
    features: [
      "8 to 12 custom designed pages with corporate typography",
      "Company vision, mission, leadership, and services overview",
      "Projects portfolio showcase & client testimonials",
      "High-resolution print-ready PDF and lightweight digital PDF",
      "Tender-ready compliance formatting"
    ],
    popular: true,
    turnaround: "48 - 72 Hours"
  },
  {
    id: "video-intro-design",
    name: "VIDEO INTRO DESIGN",
    category: "design",
    categoryLabel: "Design Services",
    priceKes: 5000,
    priceDisplay: "KES 5,000",
    description: "Dynamic 3D logo reveal and motion graphic video intro for YouTube channels, TikTok, and video ads.",
    features: [
      "Custom 3D logo animation and visual effects",
      "Royalty-free cinematic sound effects and music",
      "Delivered in 1080p Full HD & 4K MP4 format",
      "Perfect for video ads, presentations, and tutorials",
      "48-hour delivery"
    ],
    popular: true,
    turnaround: "48 Hours"
  },

  // Hosting & Management
  {
    id: "bronze-website-hosting",
    name: "Bronze Website Hosting",
    category: "hosting",
    categoryLabel: "Hosting & Management",
    priceKes: 6032,
    priceDisplay: "KES 6,032",
    pricePeriod: "yearly",
    description: "Blazing fast PCIe Gen4 NVMe LiteSpeed cloud hosting with cPanel, unlimited emails, and free SSL.",
    features: [
      "10 GB Ultra-Fast NVMe SSD Storage",
      "LiteSpeed Enterprise Web Server with LSCache",
      "FREE Lifetime Wildcard SSL Certificate",
      "Unlimited corporate business email accounts",
      "cPanel Control Panel & Imunify360 DDoS firewall",
      "99.99% Guaranteed uptime with daily backups"
    ],
    popular: true,
    turnaround: "Instant Setup"
  },
  {
    id: "lipia-pole-pole",
    name: "LIPIA POLE POLE (3 Months)",
    category: "hosting",
    categoryLabel: "Hosting & Management",
    priceKes: 6500,
    priceDisplay: "KES 6,500",
    pricePeriod: "per month (3 installments)",
    description: "Launch your dream business website today and pay comfortably across 3 flexible monthly installments.",
    features: [
      "Split total project cost across 3 equal monthly installments",
      "Website design & development begins immediately after deposit 1",
      "Domain name & high-speed NVMe hosting included",
      "Zero hidden interest fees or complicated credit checks",
      "Pay securely via Lipa na M-Pesa Paybill 542542"
    ],
    popular: true,
    lipaPolePole: true,
    turnaround: "Flexible Terms"
  },
  {
    id: "social-media-management",
    name: "Social Media Management",
    category: "hosting",
    categoryLabel: "Hosting & Management",
    priceKes: 15000,
    priceDisplay: "Starting at KES 15,000",
    pricePeriod: "monthly",
    isStartingAt: true,
    description: "Hands-free social media growth: branded promotional posters, compelling copy, and customer replies.",
    features: [
      "12 to 16 professional graphic posters & carousels per month",
      "Engaging sales copywriting tailored to Kenyan audiences",
      "Posting schedule across Facebook, Instagram, and LinkedIn",
      "Targeted Meta ad campaign management for customer leads",
      "Monthly growth analytics & inquiry reporting"
    ],
    popular: true,
    turnaround: "Monthly Retainer"
  },
  {
    id: "website-management-and-seo",
    name: "WEBSITE MANAGEMENT AND SEO",
    category: "hosting",
    categoryLabel: "Hosting & Management",
    priceKes: 35000,
    priceDisplay: "KES 35,000",
    pricePeriod: "monthly",
    description: "Total peace-of-mind webmaster retainer: technical speed optimization, Google Maps ranking, and security.",
    features: [
      "Local Google Business Profile & Google Maps 3-Pack optimization",
      "Sub-0.8s Core Web Vitals speed tuning & caching maintenance",
      "Weekly offsite cloud database backups & plugin updates",
      "Continuous Imunify360 malware scanning & firewall defense",
      "Monthly content additions, banner updates, and SEO audit report"
    ],
    popular: true,
    turnaround: "Monthly Retainer"
  }
];

export const DOMAIN_PRICING: DomainPricing[] = [
  { extension: ".co.ke", register: 1000, transfer: 1000, renew: 1400, popular: true, bestFor: "Kenyan businesses & institutions (Discounted)" },
  { extension: ".ke", register: 2700, transfer: 2700, renew: 2700, bestFor: "Short, modern Kenyan brands" },
  { extension: ".com", register: 2625, transfer: 2625, renew: 2625, popular: true, bestFor: "Global-facing companies" },
  { extension: ".net", register: 2700, transfer: 2700, renew: 2700, bestFor: "Tech & network infrastructure" },
  { extension: ".org", register: 2625, transfer: 2625, renew: 2625, bestFor: "NGOs, charities & associations" },
  { extension: ".africa", register: 2400, transfer: 2400, renew: 2400, bestFor: "Pan-African continental presence" },
  { extension: ".info", register: 1425, transfer: 6000, renew: 6000, bestFor: "Information and resource hubs" },
  { extension: ".biz", register: 2250, transfer: 2250, renew: 2250, bestFor: "Commercial and trade operations" },
];

export const HOSTING_PLANS: HostingPlan[] = [
  {
    id: "hosting-only",
    name: "Hosting Only",
    priceYear: 3875,
    targetAudience: "Starters who already own a domain",
    specs: {
      disk: "35 GB NVMe SSD",
      ram: "4 GB RAM LiteSpeed server",
      domain: "Bring your own domain",
      ssl: "FREE Wildcard SSL certificate",
      email: "Unlimited business email accounts",
      bandwidth: "Unlimited monthly bandwidth",
      websites: "Host unlimited websites",
      cpanel: true,
      mpesa: true,
      backups: "Weekly automated backups",
    },
    features: [
      "35 GB NVMe SSD disk",
      "4 GB RAM LiteSpeed server",
      "FREE SSL certificate",
      "SEO-friendly server stack",
      "Unlimited email accounts",
      "Unlimited monthly bandwidth",
      "Host unlimited websites",
      "300+ one-click script installer",
      "FREE cPanel access",
      "Node.js & Python support",
      "FREE M-Pesa integration",
      "Instant account set-up",
      "300,000 inodes allowance",
    ],
  },
  {
    id: "starter",
    name: "Starter Package",
    priceYear: 6200,
    targetAudience: "The ideal starting point for a new business site",
    specs: {
      disk: "35 GB NVMe SSD",
      ram: "4 GB RAM LiteSpeed server",
      domain: "FREE .co.ke / .com / .org domain",
      ssl: "FREE SSL certificate included",
      email: "Unlimited business emails",
      bandwidth: "Unlimited monthly bandwidth",
      websites: "Host unlimited websites",
      cpanel: true,
      mpesa: true,
      backups: "Daily automated cloud backups",
    },
    features: [
      "FREE .co.ke / .com / .org domain included",
      "35 GB NVMe SSD disk",
      "4 GB RAM LiteSpeed server",
      "FREE SSL certificate",
      "SEO-friendly hosting setup",
      "Unlimited email accounts",
      "Unlimited monthly bandwidth",
      "Host unlimited websites",
      "300+ one-click script installer",
      "FREE cPanel control panel",
      "Node.js & Python support",
      "FREE M-Pesa payment gateway hook",
      "24/7 Nairobi-based tech support",
    ],
  },
  {
    id: "standard",
    name: "Standard Package",
    priceYear: 7200,
    badge: "Most popular",
    targetAudience: "Suitable for e-commerce and booking sites",
    specs: {
      disk: "100 GB NVMe SSD",
      ram: "8 GB RAM LiteSpeed server",
      domain: "FREE .co.ke / .com / .org domain",
      ssl: "FREE SSL certificate with HTTP/3",
      email: "Unlimited accounts + webmail",
      bandwidth: "Unlimited monthly bandwidth",
      websites: "Host unlimited websites",
      cpanel: true,
      mpesa: true,
      backups: "Daily automated off-site backups",
    },
    features: [
      "100 GB NVMe SSD disk space",
      "FREE .co.ke / .com / .org domain",
      "8 GB RAM LiteSpeed server",
      "FREE SSL certificate",
      "SEO-friendly architecture",
      "Daily automated cloud backups",
      "Priority Nairobi engineer support",
      "Unlimited email accounts",
      "Unlimited monthly bandwidth",
      "Host unlimited websites",
      "WooCommerce & M-Pesa optimized",
      "Advanced spam and DDoS shield",
      "Node.js, Python & Ruby runtime",
    ],
  },
  {
    id: "business-vps",
    name: "Managed Cloud VPS",
    priceYear: 14500,
    badge: "Enterprise",
    targetAudience: "High-traffic portals, fintechs & large catalogs",
    specs: {
      disk: "Unlimited NVMe SSD Storage",
      ram: "16 GB Dedicated RAM",
      domain: "FREE premium domain + static IP",
      ssl: "Dedicated EV SSL Certificate",
      email: "Dedicated mail relay IP",
      bandwidth: "Unmetered 1 Gbps port",
      websites: "Host unlimited enterprise sites",
      cpanel: true,
      mpesa: true,
      backups: "Hourly snapshot restore points",
    },
    features: [
      "Dedicated 16 GB RAM + 8 vCPU cores",
      "Unlimited NVMe SSD enterprise array",
      "Dedicated IPv4 address in East Africa",
      "99.99% Guaranteed uptime SLA",
      "Full cPanel / WHM root administration",
      "Custom Redis & Memcached cache layers",
      "Dedicated DevOps engineer on WhatsApp",
      "Zero-downtime server migration service",
      "Custom security hardening & firewall",
      "Compliant with Kenyan Data Protection Act",
    ],
  },
];

export const SEO_FEATURES = [
  {
    id: "tech-audit",
    title: "Technical SEO Audits",
    description: "Core Web Vitals, crawlability, indexation, broken links, canonicalization, and schema graphs fixed directly at the code root.",
    highlight: "Fix indexation blockers & speed penalties",
  },
  {
    id: "local-seo",
    title: "Local SEO in Kenya",
    description: "Google Business Profile optimization, local geotagged landing pages across Nairobi, Mombasa, Kisumu, Nakuru, and local citation networks.",
    highlight: "Dominate Google Maps & 3-Pack rankings",
  },
  {
    id: "content-keywords",
    title: "Content & Keywords",
    description: "Deep competitor gap analysis and search-intent content clusters that rank for high-intent transactional and commercial queries.",
    highlight: "Capture customers ready to buy right now",
  },
  {
    id: "ai-search",
    title: "AI Search Visibility",
    description: "Structured entity schemas, knowledge graph validation, and citation anchors so Gemini, ChatGPT, and Perplexity quote your brand as the authority.",
    highlight: "Future-proof against AI answer engines",
  },
];

export const OPERATING_SYSTEM_STEPS = [
  {
    step: "01",
    name: "Discover",
    tagline: "Deep audit of business, customers and constraints.",
    detail: "We audit your existing digital footprint, customer journey bottlenecks, competitor positioning, and technical debt before writing a single line of code.",
  },
  {
    step: "02",
    name: "Strategize",
    tagline: "Roadmap, KPIs and a technology bet to win the next 18 months.",
    detail: "We define precise, measurable metrics (CAC reduction, lead conversion velocity, server response time) and formulate an actionable technology roadmap.",
  },
  {
    step: "03",
    name: "Design",
    tagline: "Executive-grade product and brand systems.",
    detail: "High-craft UI/UX tailored for user conversion, clean typography, responsive design, and design system tokens ready for seamless engineering.",
  },
  {
    step: "04",
    name: "Develop",
    tagline: "Build with rigor — typed code, observability, secure by default.",
    detail: "Engineered with production-grade TypeScript, modern frameworks, automated test suites, clean APIs, and regional infrastructure compliance.",
  },
  {
    step: "05",
    name: "Launch",
    tagline: "Go-to-market choreography, analytics and SEO baseline.",
    detail: "Rigorous staging validation, zero-downtime DNS deployment, search engine indexation submission, and comprehensive event telemetry.",
  },
  {
    step: "06",
    name: "Scale",
    tagline: "Iterate on signal. Compounding growth becomes the default.",
    detail: "Continuous optimization based on real user session recordings, A/B landing experiments, speed audits, and organic ranking expansion.",
  },
];

export const INDUSTRIES: IndustryItem[] = [
  {
    id: "healthcare",
    name: "Healthcare",
    iconName: "Stethoscope",
    subtitle: "Hospitals, Clinics & Healthtech",
    description: "Patient appointment scheduling, doctor directory search, telemedicine portals, and HIPAA/Kenyan data privacy compliance.",
    solutionsProvided: ["Automated patient booking", "Doctor credential verification", "Mobile-first symptom triage", "Secure lab result downloads"],
    sampleMetric: "+180% patient bookings via mobile",
  },
  {
    id: "hospitality",
    name: "Hospitality",
    iconName: "Hotel",
    subtitle: "Hotels, Resorts & Lodges",
    description: "Direct reservation engines that bypass hefty OTA commissions, room virtual showcases, dynamic seasonal pricing, and guest concierge.",
    solutionsProvided: ["Direct booking engine", "Integrated M-Pesa deposit", "High-res room gallery", "Multilingual guest navigation"],
    sampleMetric: "42% reduction in third-party OTA commissions",
  },
  {
    id: "education",
    name: "Education",
    iconName: "GraduationCap",
    subtitle: "Universities, Academies & EdTech",
    description: "Student admission workflows, course catalogs with instant brochure downloads, fee payment portals, and campus event hubs.",
    solutionsProvided: ["Online admission applications", "Tuition fee payment gateway", "Course syllabus downloaders", "Faculty & research archives"],
    sampleMetric: "3.2x qualified student enrollment enquiries",
  },
  {
    id: "ngos",
    name: "NGOs",
    iconName: "HeartHandshake",
    subtitle: "Non-Profits & International Aid",
    description: "Transparent impact reporting, donor contribution channels, grant documentation hubs, and multi-country field project showcases.",
    solutionsProvided: ["Multi-currency donor checkout", "Annual report interactive viewers", "Community case storyboards", "Global grant compliance pages"],
    sampleMetric: "99.99% uptime during global fundraising campaigns",
  },
  {
    id: "churches",
    name: "Churches",
    iconName: "Building2",
    subtitle: "Ministries & Faith Organizations",
    description: "Livestream integration, sermon media libraries with audio podcast feeds, ministry event registrations, and tithe/giving systems.",
    solutionsProvided: ["Direct tithe & offering portal", "Sermon audio/video archive", "Ministry volunteer sign-ups", "Weekly bulletin automation"],
    sampleMetric: "65% increase in recurring digital giving",
  },
  {
    id: "corporate",
    name: "Corporate",
    iconName: "Briefcase",
    subtitle: "Conglomerates & Financial Services",
    description: "Institutional credibility surfaces, investor relations portals, regulatory disclosure centers, and procurement partner portals.",
    solutionsProvided: ["Investor relations center", "Procurement tender submissions", "Executive leadership profiles", "Whistleblower reporting"],
    sampleMetric: "Meets international ISO & compliance standards",
  },
  {
    id: "government",
    name: "Government",
    iconName: "Landmark",
    subtitle: "Public Agencies & Parastatals",
    description: "Accessible citizen portals, public tender noticeboards, official gazette publications, and high-concurrency public servers.",
    solutionsProvided: ["WCAG 2.1 AA accessibility", "Tender document distribution", "Citizen service request desk", "Tamper-evident public records"],
    sampleMetric: "Handled 500k+ concurrent citizen visitors",
  },
  {
    id: "real-estate",
    name: "Real Estate",
    iconName: "Home",
    subtitle: "Developers & Property Agencies",
    description: "Interactive property listing search with geolocation filters, floor plan downloads, schedule site visit booking, and CRM integration.",
    solutionsProvided: ["Filterable property catalog", "Instant site visit scheduling", "Automated WhatsApp lead alerts", "Virtual unit tour player"],
    sampleMetric: "4x increase in pre-screened property viewings",
  },
  {
    id: "retail",
    name: "Retail",
    iconName: "ShoppingBag",
    subtitle: "E-Commerce & Commercial Brands",
    description: "Omnichannel inventory sync, lightning-fast checkout with M-Pesa STK push, dispatch tracking, and customer loyalty engines.",
    solutionsProvided: ["Instant M-Pesa STK push checkout", "Real-time stock level synchronization", "Automated dispatch SMS", "Abandoned cart automation"],
    sampleMetric: "68% checkout completion rate via M-Pesa",
  },
  {
    id: "startups",
    name: "Startups",
    iconName: "Rocket",
    subtitle: "Early-Stage & Seed Ventures",
    description: "Rapid MVP engineering, high-converting product waitlists, investor pitch platforms, and scalable cloud foundations.",
    solutionsProvided: ["2-week MVP builds", "Interactive investor decks", "Product hunt launch prep", "Scalable database architecture"],
    sampleMetric: "Helped startups raise over $4.2M in seed capital",
  },
];

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "shantis-designs",
    category: "Websites · Solar Energy & E-Commerce",
    client: "Shantis Designs",
    domain: "shantisdesigns.co.ke",
    domainSnippet: {
      tagline: "Commercial & Residential Solar Energy Systems in Kenya",
      keyFeatures: [
        "Tier-1 Monocrystalline Solar Panels & Hybrid Inverters E-Shop",
        "Interactive kW Solar Sizing & Battery Load Calculator",
        "Direct Lipa na M-Pesa STK Push Online Checkout",
        "Nairobi, Central & Rift Valley Installation Teams"
      ],
      liveUrl: "https://shantisdesigns.co.ke"
    },
    title: "Solar installation and clean energy product e-commerce platform",
    summary: "E-commerce store for solar panels, hybrid inverters, batteries, and instant sizing calculator with M-Pesa payments.",
    description: "Shantis Designs is a premier solar installation and clean energy product retailer in Kenya. We engineered their modern e-commerce storefront allowing residential homeowners and commercial enterprises to size solar systems, purchase Tier-1 solar panels, hybrid inverters, and lithium iron phosphate (LiFePO4) storage, with automated M-Pesa STK push checkout and technician site visit booking.",
    industry: "Solar Energy & Clean Tech",
    metrics: [
      { label: "Online Solar Orders", value: "+340%" },
      { label: "Solar Quote Speed", value: "< 2 mins" },
      { label: "Direct Web Revenue", value: "KSh 14.8M" },
    ],
    deliverables: ["Solar e-commerce store", "Instant kW load & battery calculator", "M-Pesa Daraja payment integration", "Technician dispatch portal"],
    techStack: ["Next.js", "TypeScript", "Tailwind CSS", "M-Pesa Daraja", "PostgreSQL"],
    accentColor: "from-[#b88e4c] via-[#0077b5] to-slate-950",
    imageUrl: "/images/shantis_solar_ecommerce_1790951316846.webp",
  },
  {
    id: "jaymax-auto",
    category: "Websites · Automotive E-Commerce & Spare Parts",
    client: "Jaymax Auto Spares",
    domain: "jaymaxauto.co.ke",
    domainSnippet: {
      tagline: "Kenya's Premier Auto AC & Spare Parts Online Store",
      keyFeatures: [
        "AC Compressors, Condensers & Evaporators E-Shop",
        "Lipa na M-Pesa Automated Checkout with Daraja 2.0",
        "Same-Day Dispatch & Delivery in Nairobi",
        "Trusted by Mechanics & Garages Countrywide"
      ],
      liveUrl: "https://jaymaxauto.co.ke"
    },
    title: "Automotive AC parts & replacement spares e-commerce platform",
    summary: "Complete online auto spares shop with vehicle search, instant M-Pesa checkout, and courier dispatch tracking.",
    description: "Jaymax Auto is a top automotive parts specialist based in Nairobi, Kenya, offering premium AC compressors, condensers, radiators, and brake rotors. We engineered their high-speed e-commerce portal with WooCommerce, LiteSpeed caching, and automated Lipa na M-Pesa integration.",
    industry: "Automotive E-Commerce",
    metrics: [
      { label: "Online Spares Orders", value: "+310%" },
      { label: "M-Pesa Checkout Time", value: "< 25s" },
      { label: "Catalog Searches/Mo", value: "45,000+" },
    ],
    deliverables: ["WooCommerce e-shop", "Vehicle parts finder & SKU filter", "Safaricom M-Pesa Daraja payment", "Courier order tracking"],
    techStack: ["WordPress", "WooCommerce", "LiteSpeed NVMe", "M-Pesa Daraja"],
    accentColor: "from-blue-700 via-slate-900 to-slate-950",
    imageUrl: "/images/jaymax_auto_ecommerce.webp",
  },
  {
    id: "sprinkville-networks",
    category: "Websites · Telecommunications & Fiber ISP",
    client: "Sprinkville Networks",
    domain: "sprinkvillenetworks.co.ke",
    domainSnippet: {
      tagline: "High-Speed Residential & Enterprise Fiber Internet",
      keyFeatures: [
        "Instant Fiber Coverage Map Check by Estate",
        "Bandwidth Package Comparison & Online Sign-up",
        "Automated Technician Installation Dispatch",
        "Sub-0.5s Page Load on Safaricom & Airtel 4G"
      ],
      liveUrl: "https://www.sprinkvillenetworks.co.ke"
    },
    title: "ISP website built for coverage checks and fast customer sign-ups",
    summary: "Package pages and coverage lookup that turn visitors into internet installation requests.",
    description: "Sprinkville required a high-conversion digital portal for their fiber-to-the-home and business internet services. We engineered an interactive coverage checker, customized residential and commercial bandwidth tiers, and automated installation enquiry routing that doubled sales throughput.",
    industry: "Internet & Telecom",
    metrics: [
      { label: "Sign-up conversion", value: "+320%" },
      { label: "Coverage check speed", value: "<150ms" },
      { label: "Dispatch cycle", value: "-45%" },
    ],
    deliverables: ["Coverage map lookup", "Package comparison engine", "Automated ticket dispatch", "Core Web Vitals 99/100"],
    techStack: ["Next.js", "TypeScript", "Tailwind CSS", "PostgreSQL", "M-Pesa Daraja"],
    accentColor: "from-blue-600 to-indigo-800",
    imageUrl: "/images/hero_digital_infrastructure_1790951298995.webp",
  },
  {
    id: "mtandao-afritech",
    category: "Websites · Technology & Enterprise IT",
    client: "Mtandao Afritech Solutions",
    domain: "mtandaoafritechsolutions.com",
    domainSnippet: {
      tagline: "Pan-African Enterprise IT, Cloud & Cybersecurity Services",
      keyFeatures: [
        "Structured B2B Solutions Taxonomy (Cloud, Cyber & Networks)",
        "Interactive Corporate RFP & Tender Scope Builder",
        "Verified East Africa Enterprise Case Study Archive",
        "Direct C-Suite Consultation Intake Funnels"
      ],
      liveUrl: "https://www.mtandaoafritechsolutions.com"
    },
    title: "Technology services website with a clear enterprise solutions architecture",
    summary: "Every service reachable in two clicks, with an enquiry path on each solutions page.",
    description: "An enterprise IT solutions provider needed to articulate complex cybersecurity, networking, and cloud migration services to C-suite procurement officers across East Africa.",
    industry: "Enterprise IT & Cloud",
    metrics: [
      { label: "B2B RFP Submissions", value: "+210%" },
      { label: "Average Time on Site", value: "3m 45s" },
      { label: "Bounce Rate", value: "28%" },
    ],
    deliverables: ["Solutions taxonomy", "Interactive RFP generator", "Case study archive", "Lead qualification funnels"],
    techStack: ["React", "Tailwind CSS", "Node.js", "HubSpot CRM Webhook"],
    accentColor: "from-[#009fe3] to-slate-900",
    imageUrl: "/images/cloud_enterprise_servers_1790953244864.webp",
  },
  {
    id: "parklane-africa",
    category: "Websites · Corporate & Pan-African Trade",
    client: "Parklane Africa",
    domain: "parklaneafrica.com",
    domainSnippet: {
      tagline: "Commercial Trade, Strategic Investments & Pan-African Partnerships",
      keyFeatures: [
        "Institutional Credibility Surface & Partner Directory",
        "Governance, Compliance & ESG Documentation Library",
        "Multi-Country Commercial Trading Scale Showcase",
        "Sub-0.8s Global Mobile Speed Index"
      ],
      liveUrl: "https://parklaneafrica.com"
    },
    title: "Corporate website for a pan-African commercial trading brand",
    summary: "A credible brand surface that stands up to procurement and institutional partner review.",
    description: "Parklane Africa needed a corporate digital headquarters reflecting their multi-country operations, institutional partnerships, and commercial trading scale across Africa.",
    industry: "Corporate & Trade",
    metrics: [
      { label: "Corporate credibility score", value: "A+" },
      { label: "Institutional enquiries", value: "+185%" },
      { label: "Mobile speed index", value: "0.8s" },
    ],
    deliverables: ["Corporate storytelling", "Governance & compliance library", "Multi-country partner directory", "Bespoke typography"],
    techStack: ["React", "TypeScript", "Tailwind CSS", "LiteSpeed NVMe"],
    accentColor: "from-slate-800 to-slate-950",
    imageUrl: "/images/strategic_founders_advisory_1790953255396.webp",
  },
  {
    id: "pj-chuma",
    category: "Websites · Building Supplies & Hardware Wholesale",
    client: "PJ Chuma",
    domain: "pjchuma.co.ke",
    domainSnippet: {
      tagline: "Kenya's Premier Wholesale Hardware & Construction Steel Supplier",
      keyFeatures: [
        "2,400+ Building Materials & Steel Products Catalog",
        "Instant Contractor RFQ & Bill of Quantities Quote Builder",
        "Direct WhatsApp Order Sync with Warehouse Staff",
        "Nairobi & Upcountry Bulk Dispatch Tracking"
      ],
      liveUrl: "https://pjchuma.co.ke"
    },
    title: "Hardware, structural steel and building supplies website",
    summary: "Product categories and quote requests replace repetitive phone and counter enquiries.",
    description: "Transformed a traditional hardware wholesaler into a digital catalog powerhouse where contractors can build material lists and generate instant quotations directly online.",
    industry: "Construction & Wholesale",
    metrics: [
      { label: "Digital Quote Requests", value: "+440%" },
      { label: "Sales Team Hours Saved", value: "25 hrs/wk" },
      { label: "Catalog items indexed", value: "2,400+" },
    ],
    deliverables: ["B2B catalog browser", "Instant RFQ cart system", "WhatsApp order sync", "Local warehouse stock badges"],
    techStack: ["React", "Vite", "Tailwind CSS", "WhatsApp Business API"],
    accentColor: "from-amber-600 to-orange-800",
    imageUrl: "/images/store_pos_hardware_1790951352250.webp",
  },
  {
    id: "topsec-technologies",
    category: "Websites · Security & Biometrics",
    client: "Topsec Technologies",
    domain: "topsectechnologies.co.ke",
    domainSnippet: {
      tagline: "Commercial Security, CCTV, Biometric Access & Electric Fencing",
      keyFeatures: [
        "Premises Security Assessment Quiz & Quote Estimator",
        "Technical Datasheets for Hikvision & Dahua Hardware",
        "Emergency Technical Dispatch Hotline Integration",
        "Local SEO Pages for Nairobi, Thika & Nakuru"
      ],
      liveUrl: "https://topsectechnologies.co.ke"
    },
    title: "Security technology website with solution-led customer navigation",
    summary: "Site-survey enquiries arrive with the exact security system scale already identified.",
    description: "Engineered a solution-first customer journey for commercial CCTV, biometric access control, and electric fencing where leads pre-select their premises scale.",
    industry: "Physical Security & IoT",
    metrics: [
      { label: "Qualified Site Audits", value: "+290%" },
      { label: "Commercial Close Rate", value: "38%" },
      { label: "Search impressions", value: "+400%" },
    ],
    deliverables: ["Premises assessment quiz", "Technical spec sheets", "Emergency dispatch hotline", "SEO city landing pages"],
    techStack: ["Next.js", "TypeScript", "Tailwind CSS", "Google Tag Manager"],
    accentColor: "from-blue-700 to-cyan-900",
    imageUrl: "/images/cloud_enterprise_servers_1790953244864.webp",
  },
  {
    id: "nairobi-west-spa",
    category: "Websites · Luxury Wellness & Spa",
    client: "Nairobi West Spa",
    domain: "nairobiwestspa.co.ke",
    domainSnippet: {
      tagline: "Exclusive Wellness, Body Massages & Holistic Therapies",
      keyFeatures: [
        "Sensory Interactive Treatment & Package Menu",
        "Real-Time Therapist Calendar & Time Slot Reservation",
        "Automated SMS Appointment Confirmations (Africa's Talking)",
        "Secure Lipa na M-Pesa Booking Deposit Processing"
      ],
      liveUrl: "https://nairobiwestspa.co.ke"
    },
    title: "Luxury spa and wellness website designed around instant client bookings",
    summary: "Treatment menu and booking enquiry available from every page with zero phone lag.",
    description: "Designed a tranquil, luxurious booking experience for high-end wellness treatments with real-time slot selection and instant deposit confirmation.",
    industry: "Luxury Wellness & Spa",
    metrics: [
      { label: "Online bookings share", value: "82%" },
      { label: "Weekend capacity", value: "100%" },
      { label: "No-show reduction", value: "-64%" },
    ],
    deliverables: ["Sensory visual design", "Interactive treatment menu", "Automated SMS appointment reminders", "M-Pesa deposit processing"],
    techStack: ["React", "Tailwind CSS", "M-Pesa STK Push", "Africa's Talking SMS"],
    accentColor: "from-rose-600 to-pink-900",
    imageUrl: "/images/nairobiwest_spa_hero.webp",
  },
  {
    id: "ck-law-advocates",
    category: "Websites · Legal & Corporate Law",
    client: "CK Law Advocates",
    domain: "cklaw.co.ke",
    domainSnippet: {
      tagline: "Corporate Commercial, Conveyancing & Litigation Law Firm",
      keyFeatures: [
        "Comprehensive Kenyan Legal Practice Area Knowledge Base",
        "Senior Advocate Credentials & Bar Admissions Directory",
        "End-to-End Encrypted Consultation Briefing Form",
        "Rank #1 on Google for Corporate Lawyers Nairobi"
      ],
      liveUrl: "https://cklaw.co.ke"
    },
    title: "Law firm website with deep practice-area knowledge architecture",
    summary: "Practice-area pages that answer client questions before the first consultation.",
    description: "Created an authoritative legal authority platform with detailed guidance on corporate law, land transactions, intellectual property, and litigation in Kenya.",
    industry: "Legal & Professional Services",
    metrics: [
      { label: "Consultation Requests", value: "+175%" },
      { label: "Top 3 Google Legal Rankings", value: "14 keywords" },
      { label: "Client retention", value: "94%" },
    ],
    deliverables: ["Practice area knowledge base", "Attorney profile credentials", "Encrypted case consultation form", "Legal insights blog"],
    techStack: ["React", "TypeScript", "Tailwind CSS", "Structured Schema.org"],
    accentColor: "from-indigo-900 to-slate-900",
    imageUrl: "/images/strategic_founders_advisory_1790953255396.webp",
  },
  {
    id: "fancy-touch",
    category: "Websites · Beauty & Fashion",
    client: "Fancy Touch",
    domain: "fancytouch.co.ke",
    domainSnippet: {
      tagline: "Premier Hair Styling, Beauty Salon & Luxury Aesthetics",
      keyFeatures: [
        "Visual Before & After Lookbook Hair Portfolio",
        "Interactive Stylist Booking & Service Menu Bundles",
        "Online Gift Card & Beauty Package Purchase",
        "Direct Instagram Traffic Conversion Funnels"
      ],
      liveUrl: "https://fancytouch.co.ke"
    },
    title: "Beauty and styling website with a bookable service menu",
    summary: "Appointment enquiries captured directly from the service menu.",
    description: "A trendy beauty salon portal allowing clients to select stylists, inspect before-and-after portfolios, and book sessions without phone back-and-forth.",
    industry: "Beauty & Lifestyle",
    metrics: [
      { label: "Daily appointment bookings", value: "45+" },
      { label: "Instagram traffic conversion", value: "+310%" },
    ],
    deliverables: ["Interactive stylist calendar", "Visual lookbook gallery", "Service package bundles", "Gift card purchase"],
    techStack: ["React", "Tailwind CSS", "Vite"],
    accentColor: "from-fuchsia-600 to-purple-900",
    imageUrl: "/images/nairobiwest_spa_hero.webp",
  },
  {
    id: "bdit-college",
    category: "Websites · Education & TVET College",
    client: "BDIT College",
    domain: "bditcollege.org",
    domainSnippet: {
      tagline: "Business, Information Technology & Technical Training College",
      keyFeatures: [
        "Online Course Catalog with TVETA & KNEC Accredited Programs",
        "Instant Student Application & Document Submission Portal",
        "Academic Term Fee Schedules & Lipa na M-Pesa Integration",
        "Campus Virtual Tour & Career Counseling Scheduler"
      ],
      liveUrl: "https://bditcollege.org"
    },
    title: "Academic portal and student admissions website for technical training",
    summary: "Online course applications and fee schedules that streamline student enrollment.",
    description: "BDIT College needed a high-performance academic portal to recruit students across Kenya. We engineered an online prospectus, course finder, automated enrollment forms, and M-Pesa tuition fee guidelines.",
    industry: "Higher Education & TVET",
    metrics: [
      { label: "Online Admissions", value: "+280%" },
      { label: "Course Guide Downloads", value: "8,500+" },
      { label: "Mobile Load Speed", value: "0.9s" },
    ],
    deliverables: ["Course directory", "Online admission forms", "Campus calendar", "SEO academic rankings"],
    techStack: ["Next.js", "TypeScript", "Tailwind CSS", "PostgreSQL"],
    accentColor: "from-emerald-700 to-teal-950",
    imageUrl: "/images/hero_digital_infrastructure_1790951298995.webp",
  },
  {
    id: "minerock-geo",
    category: "Websites · Engineering & Geological Consulting",
    client: "Mine Rock Geological Consultants",
    domain: "minerockgeoconsultants.com",
    domainSnippet: {
      tagline: "Hydrogeology, Groundwater Exploration & Geotechnical Surveys",
      keyFeatures: [
        "Borehole Hydrogeological Survey & Water Resource Assessment",
        "NEMA Environmental Impact Assessment (EIA) Certification",
        "Mineral Exploration, Core Drilling & Laboratory Testing",
        "Interactive Project Inquiry & Technical Report Intake"
      ],
      liveUrl: "https://minerockgeoconsultants.com"
    },
    title: "Engineering and geotechnical consultancy authority platform",
    summary: "Geological survey, EIA report, and borehole drilling request platform.",
    description: "Mine Rock Geo-Consultants is an engineering firm specializing in groundwater hydrogeology, EIA environmental licensing, and mineral exploration. We built their digital platform to showcase technical credentials to commercial developers, mining firms, and agricultural syndicates.",
    industry: "Engineering & Geology",
    metrics: [
      { label: "B2B Survey RFPs", value: "+225%" },
      { label: "Average Project Value", value: "KSh 1.2M" },
      { label: "Technical Credentials Indexed", value: "100%" },
    ],
    deliverables: ["Geological service taxonomy", "Technical credential library", "Borehole quote generator", "Client portal intake"],
    techStack: ["React", "TypeScript", "Tailwind CSS", "LiteSpeed"],
    accentColor: "from-amber-700 to-stone-950",
    imageUrl: "/images/hero_digital_infrastructure_1790951298995.webp",
  },
  {
    id: "raylights-solar",
    category: "Websites · Solar Water Heating & Renewable Energy",
    client: "Raylights Solar",
    domain: "raylightssolar.co.ke",
    domainSnippet: {
      tagline: "Solar Water Heaters, PV Energy Systems & Power Backup",
      keyFeatures: [
        "Pressurized & Non-Pressurized Solar Water Heating Catalog",
        "Energy Commission EPRA Compliant Solar Specifications",
        "Payback ROI Estimator for Residential & Commercial Facilities",
        "Direct M-Pesa Deposit & Free Installation Site Booking"
      ],
      liveUrl: "https://raylightssolar.co.ke"
    },
    title: "Clean energy equipment e-commerce and commercial solar water heating platform",
    summary: "EPRA-compliant solar water heating and solar PV systems with online quote calculators.",
    description: "Raylights Solar supplies solar thermal and photovoltaic equipment across Kenya. We engineered their product showroom with clear technical datasheets, warranty credentials, and automated quotation pipelines.",
    industry: "Clean Tech & Solar Energy",
    metrics: [
      { label: "Solar Heater Sales", value: "+310%" },
      { label: "Residential Enquiries", value: "65/wk" },
      { label: "Central Kenya Reach", value: "+190%" },
    ],
    deliverables: ["Solar catalog", "Sizing calculator", "M-Pesa payment integration", "Warranty tracker"],
    techStack: ["WordPress", "WooCommerce", "LiteSpeed NVMe"],
    accentColor: "from-amber-600 via-orange-800 to-slate-950",
    imageUrl: "/images/shantis_solar_ecommerce_1790951316846.webp",
  },
  {
    id: "helios-solar",
    category: "Websites · Commercial Solar EPC & Power Systems",
    client: "Helios Solar Power",
    domain: "helios.co.ke",
    domainSnippet: {
      tagline: "Industrial Solar EPC, Lithium Storage & Grid-Tied Systems",
      keyFeatures: [
        "Commercial & Industrial (C&I) Rooftop Solar EPC Portfolios",
        "Lithium Storage & Diesel Generator Synchronizer Integration",
        "Live Solar Generation Data & Carbon Offset Dashboard",
        "Enterprise Power Purchase Agreement (PPA) Advisory"
      ],
      liveUrl: "https://helios.co.ke"
    },
    title: "Commercial solar engineering and industrial power platform",
    summary: "Industrial solar solutions platform showcasing commercial energy savings and project portfolios.",
    description: "Helios Solar Power engineers large-scale solar installations for factories, commercial farms, and residential estates. We created their executive corporate portal articulating CAPEX savings and PPA financial models.",
    industry: "Renewable Energy EPC",
    metrics: [
      { label: "Industrial EPC Leads", value: "+260%" },
      { label: "MegaWatt Capacity Showcased", value: "8.4 MW" },
      { label: "Audit Turnaround", value: "24 Hours" },
    ],
    deliverables: ["Industrial solar portfolio", "Carbon offset calculator", "Financial PPA modeler", "Corporate storytelling"],
    techStack: ["React", "TypeScript", "Tailwind CSS", "Motion"],
    accentColor: "from-yellow-600 to-slate-950",
    imageUrl: "/images/shantis_solar_ecommerce_1790951316846.webp",
  },
  {
    id: "little-known-expeditions",
    category: "Websites · Travel & Luxury Safaris",
    client: "The Little Known Expeditions",
    domain: "littleknownexpeditions.co.ke",
    domainSnippet: {
      tagline: "Curated Kenyan Wildlife Safaris, Eco-Tours & Expeditions",
      keyFeatures: [
        "Interactive Day-by-Day Maasai Mara & Amboseli Itineraries",
        "Bespoke Safari Inquiry Builder with International Currency Sync",
        "Wildlife Photography & Conservation Partner Showcase",
        "Multi-lingual Support for European & North American Travelers"
      ],
      liveUrl: "https://littleknownexpeditions.co.ke"
    },
    title: "Expedition and adventure travel website with interactive itineraries",
    summary: "Trip pages that pre-qualify travellers before they enquire.",
    description: "Curated safari and remote wilderness itinerary experiences designed to inspire affluent international travelers, featuring detailed route maps, lodge amenities, and gear checklists.",
    industry: "Travel & Ecotourism",
    metrics: [
      { label: "International traveler leads", value: "+380%" },
      { label: "Average booking value", value: "$4,800" },
    ],
    deliverables: ["Day-by-day interactive itineraries", "Custom safari inquiry builder", "Currency auto-converter", "Offline travel guides"],
    techStack: ["React", "Tailwind CSS", "Motion"],
    accentColor: "from-[#b88e4c] to-slate-900",
    imageUrl: "/images/strategic_founders_advisory_1790953255396.webp",
  },
  {
    id: "zuki",
    category: "Websites · Commerce & Lifestyle Brands",
    client: "Zuki",
    domain: "zuki.co.ke",
    domainSnippet: {
      tagline: "Contemporary African Lifestyle & Consumer Goods Store",
      keyFeatures: [
        "Curated Consumer Product Catalog with Rich Media Imagery",
        "One-Click Safaricom Daraja Lipa na M-Pesa STK Prompt",
        "Automated Order Dispatch Tracking & SMS Receipts",
        "Fast 1.2s Mobile Checkout Flow on Safaricom 4G"
      ],
      liveUrl: "https://zuki.co.ke"
    },
    title: "High-converting lifestyle brand e-commerce storefront",
    summary: "Products presented consistently with a direct 1-click M-Pesa checkout.",
    description: "Modern lifestyle and consumer product showcase built with sleek typography and lightning-fast checkout flow.",
    industry: "Consumer Goods",
    metrics: [
      { label: "Mobile checkout speed", value: "1.2s" },
      { label: "Cart abandonment rate", value: "18%" },
    ],
    deliverables: ["Product catalog with filters", "One-click M-Pesa checkout", "Social proof reviews", "Order status tracking"],
    techStack: ["React", "Tailwind CSS", "M-Pesa API"],
    accentColor: "from-red-600 to-rose-900",
    imageUrl: "/images/jaymax_auto_ecommerce.webp",
  },
  {
    id: "fortunas-resort",
    category: "Websites · Hospitality & Luxury Resorts",
    client: "Fortunas Resort",
    domain: "fortunasresort.co.ke",
    domainSnippet: {
      tagline: "Lakeside Luxury Resort, Conference Center & Event Venues",
      keyFeatures: [
        "Direct Room Accommodation Booking with Real-Time Rates",
        "Conference Hall & Corporate Retreat Venue Floorplans",
        "Wedding Package Planner & Dining Menu Showcase",
        "Virtual 360-Degree Property Tour Embed"
      ],
      liveUrl: "https://fortunasresort.co.ke"
    },
    title: "Lakeside resort website built around room bookings and corporate conferences",
    summary: "Reservation enquiries arrive with room type and dates attached.",
    description: "Luxury resort website featuring direct room booking, conference hall packages, and wedding event reservations.",
    industry: "Hotels & Leisure",
    metrics: [
      { label: "Direct web bookings", value: "+260%" },
      { label: "Conference enquiries", value: "+190%" },
    ],
    deliverables: ["Room type comparison", "Event venue floorplans", "Interactive meal dining menus", "Virtual 360 tour embed"],
    techStack: ["React", "Tailwind CSS", "LiteSpeed"],
    accentColor: "from-teal-600 to-cyan-950",
    imageUrl: "/images/nairobiwest_spa_hero.webp",
  },
  {
    id: "nova-talent",
    category: "Websites · HR & Executive Recruitment",
    client: "Nova Talent",
    domain: "novatalent.co.ke",
    domainSnippet: {
      tagline: "Pan-African Executive Search, Recruitment & HR Advisory",
      keyFeatures: [
        "Dedicated Candidate CV Intake & Skills Matching Engine",
        "Corporate Employer Job Requisition & Hiring Brief Builder",
        "Kenyan Industry Salary Benchmark Guides & Insights",
        "Applicant Tracking Webhook & Automated Screening"
      ],
      liveUrl: "https://novatalent.co.ke"
    },
    title: "Recruitment and executive talent platform website",
    summary: "Separate employer and candidate paths from the first screen.",
    description: "Pan-African executive recruitment and talent placement portal with dedicated candidate CV uploads and employer job vacancy posting.",
    industry: "HR & Recruitment",
    metrics: [
      { label: "Executive candidates screened", value: "12,000+" },
      { label: "Employer contract acquisitions", value: "+180%" },
    ],
    deliverables: ["Candidate CV intake portal", "Employer hiring brief builder", "Salary benchmark guides", "Applicant tracking webhook"],
    techStack: ["React", "TypeScript", "Tailwind CSS"],
    accentColor: "from-violet-700 to-indigo-950",
    imageUrl: "/images/strategic_founders_advisory_1790953255396.webp",
  },
  {
    id: "diani-eagles",
    category: "Websites · Coastal Tourism & Safaris",
    client: "Diani Eagles Tours & Safaris",
    domain: "dianieagle.co.ke",
    domainSnippet: {
      tagline: "Diani Beach Coastal Excursions, Wasini Dhow & Wildlife Safaris",
      keyFeatures: [
        "Wasini Island Dolphin Dhow Safari & Snorkeling Packages",
        "Tsavo East & West Overnight Bush Excursion Bookings",
        "Direct Lipa na M-Pesa & Card Payment Processing",
        "Multi-Currency Pricing (USD, EUR, KES) with Instant Vouchers"
      ],
      liveUrl: "https://dianieagle.co.ke"
    },
    title: "Coastal excursion, dolphin tours and safari booking portal",
    summary: "Direct tour reservations with Wasini dolphin trips, Tsavo game drives, and instant booking confirmation.",
    description: "Diani Eagles is a top tour operator in South Coast Diani Beach, Kenya, specializing in marine excursions and safari expeditions. We built their digital booking engine allowing European and local travelers to book tours with real-time seat availability and M-Pesa or credit card checkouts.",
    industry: "Coastal Tourism & Safaris",
    metrics: [
      { label: "Direct Tour Bookings", value: "+340%" },
      { label: "International Enquiries", value: "110/mo" },
      { label: "Mobile Booking Speed", value: "1.1s" },
    ],
    deliverables: ["Tour package directory", "Live seat booking calendar", "M-Pesa & Card checkout", "Customer voucher generator"],
    techStack: ["WordPress", "WooCommerce", "LiteSpeed NVMe", "M-Pesa Daraja"],
    accentColor: "from-cyan-600 via-blue-800 to-slate-950",
    imageUrl: "/images/strategic_founders_advisory_1790953255396.webp",
  },
];

export const REVIEWS: Review[] = [
  {
    id: "david-kibue",
    author: "David Kibue",
    date: "1 December 2025",
    rating: 5,
    content: "We have work together in developing my website and creation of my social media strategy and video advert creation. Hii team ni Kali sana! Highly capable and always deliver ahead of schedule.",
    ownerReply: "Looking forward to growing your business to greater horizons. It's a pleasure partnering with your team!",
    source: "Google Review · Verified by Trustindex",
  },
  {
    id: "ramadhan-otieno",
    author: "Ramadhan Otieno",
    date: "21 November 2025",
    rating: 5,
    content: "Pure skills... 10/10 website! The attention to performance, speed and responsiveness exceeded our expectations completely.",
    ownerReply: "Thank you for your feedback, Ramadhan! We highly appreciate your trust in Thinkers Arcade.",
    source: "Google Review · Verified by Trustindex",
  },
  {
    id: "trevor-muasya",
    author: "Trevor Muasya",
    date: "21 November 2025",
    rating: 5,
    content: "Used their services for our business cloud migration and web revamp. The team was quite helpful and offered great assistance throughout the launch.",
    ownerReply: "Thank you for your feedback Trevor, we are glad to keep supporting your infrastructure!",
    source: "Google Review · Verified by Trustindex",
  },
  {
    id: "joseph-muthomi",
    author: "Joseph Muthomi",
    date: "12 November 2025",
    rating: 5,
    content: "Amazing web design and SEO setup. Thanks for the outstanding service and professional support!",
    ownerReply: "Thank you for your feedback Joseph! Rooting for your continued growth.",
    source: "Google Review · Verified by Trustindex",
  },
  {
    id: "lewis-muthaura",
    author: "Lewis Muthaura",
    date: "11 August 2025",
    rating: 5,
    content: "Impressive service delivery. Mr. Tonny met the deadlines and his full involvement during the development service was awesome. I Highly recommend.",
    ownerReply: "Thank you for doing business with Thinkers Arcade. Your one stop digital marketing and SEO management Service provider.",
    source: "Google Review · Verified by Trustindex",
  },
  {
    id: "derrick",
    author: "Derrick",
    date: "7 July 2025",
    rating: 5,
    content: "Great guy to work with, timely, resourceful and ready to help. I would recommend anytime for anyone looking for serious web infrastructure.",
    ownerReply: "Thank you for your response and trusting in us Derrick!",
    source: "Google Review · Verified by Trustindex",
  },
  {
    id: "kiriamiraa-daniel",
    author: "Kiriamiraa Daniel",
    date: "7 July 2025",
    rating: 5,
    content: "Very effective and professional team. Clean work that stands out in our market.",
    ownerReply: "Looking forward to growing your business to greater horizons!",
    source: "Google Review · Verified by Trustindex",
  },
];

export const VENTURE_LAB_OFFERINGS = [
  {
    title: "MVP Development",
    desc: "Rapid full-stack prototypes built in 4 to 8 weeks, tested with real target customers across Kenya and Africa.",
    icon: "Code",
  },
  {
    title: "Investor Pitch Decks",
    desc: "Narrative architecture, unit economics modeling, and design decks crafted to withstand VC due diligence.",
    icon: "Presentation",
  },
  {
    title: "Product Design",
    desc: "Mobile-first, bandwidth-conscious UI/UX design systems optimized for high user adoption in emerging markets.",
    icon: "Palette",
  },
  {
    title: "Startup Websites",
    desc: "High-converting waitlist and launch platforms engineered for organic search visibility from day zero.",
    icon: "Globe",
  },
  {
    title: "Growth Systems",
    desc: "Customer acquisition loops, referral engines, automated email onboarding, and retention analytics.",
    icon: "TrendingUp",
  },
  {
    title: "Technical Partnerships",
    desc: "Acting as your fractional CTO and engineering team, letting founders focus 100% on sales and fundraising.",
    icon: "Handshake",
  },
];
